(function () {
  const NAV_SECTION_IDS = ['products', 'bestsellers', 'events', 'contact'];
  const DEFAULT_OFF_HOME_SECTION = 'products';
  const PENDING_SCROLL_KEY = 'gullyo:scrollTo';

  function getNavigationType() {
    const entry = performance.getEntriesByType('navigation')[0];
    return entry?.type || 'navigate';
  }

  function shouldStartHomepageAtTop() {
    return window.__gullyoStartHomepageAtTop === true;
  }

  function isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  function scrollWindowToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  }

  function ensureInitialTop() {
    if (!shouldStartHomepageAtTop()) return;

    scrollWindowToTop();
    window.requestAnimationFrame(() => {
      scrollWindowToTop();
    });
  }

  function focusWithoutScroll(element) {
    if (!element || typeof element.focus !== 'function') return;

    try {
      element.focus({ preventScroll: true });
    } catch {
      element.focus();
    }
  }

  function clearStaleBodyScrollLock() {
    const hasOpenDialog = document.querySelector('dialog[open]');
    const hasOpenDrawer = document.querySelector('header-drawer details.menu-drawer-container[open]');
    if (hasOpenDialog || hasOpenDrawer) return;

    const body = document.body;
    if (!body) return;

    if (body.style.position === 'fixed' || body.style.top) {
      body.style.width = '';
      body.style.position = '';
      body.style.top = '';
    }

    if (
      document.documentElement.hasAttribute('scroll-lock') &&
      !document.querySelector('[scroll-lock] dialog[open], [scroll-lock][open]')
    ) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  function handleInvalidHashScroll() {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash || document.getElementById(hash)) return;
    if (getNavigationType() === 'back_forward') return;
    scrollWindowToTop();
  }

  let initialScrollInitialized = false;

  function initInitialScrollSystem() {
    if (initialScrollInitialized) return;
    initialScrollInitialized = true;

    clearStaleBodyScrollLock();

    if (isDesignMode()) return;

    if (shouldStartHomepageAtTop()) {
      ensureInitialTop();

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', ensureInitialTop, { once: true });
      }

      window.addEventListener('load', ensureInitialTop, { once: true });
    }

    handleInvalidHashScroll();

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) return;
      if (!shouldStartHomepageAtTop()) return;
      ensureInitialTop();
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function scrollToSection(sectionId, behavior) {
    if (!sectionId) return false;
    const element = document.getElementById(sectionId);
    if (!element) return false;

    element.scrollIntoView({
      behavior: behavior || (prefersReducedMotion() ? 'auto' : 'smooth'),
      block: 'start',
    });
    return true;
  }

  function parseHashHref(href) {
    try {
      const url = new URL(href, window.location.origin);
      return {
        path: url.pathname || '/',
        sectionId: url.hash.replace(/^#/, ''),
      };
    } catch {
      return { path: '/', sectionId: '' };
    }
  }

  function pathsMatch(currentPath, targetPath) {
    const normalize = (path) => (path === '' || path === '/' ? '/' : path.replace(/\/$/, ''));
    return normalize(currentPath) === normalize(targetPath);
  }

  function updateHashUrl(path, sectionId) {
    const next = sectionId ? `${path === '/' ? '/' : path}#${sectionId}` : path;
    if (window.location.pathname + window.location.hash !== next) {
      window.history.replaceState(window.history.state, '', next);
    }
  }

  let hashNavigationBound = false;

  function initHashNavigation() {
    if (hashNavigationBound) return;
    hashNavigationBound = true;

    document.addEventListener('click', async (event) => {
      const link = event.target.closest('a[href*="#"]');
      if (!link || link.target === '_blank') return;

      const href = link.getAttribute('href');
      if (!href || href === '#') return;

      const { path, sectionId } = parseHashHref(href);
      if (!sectionId) return;

      if (pathsMatch(window.location.pathname, path)) {
        event.preventDefault();
        await closeMobileMenuDrawer();
        scrollToSection(sectionId);
        updateHashUrl(path, sectionId);

        if (NAV_SECTION_IDS.includes(sectionId)) {
          setActiveNavLink(sectionId);
        }
      } else {
        sessionStorage.setItem(PENDING_SCROLL_KEY, sectionId);
      }
    });

    if (isDesignMode()) return;

    const navigationType = getNavigationType();
    const pending = sessionStorage.getItem(PENDING_SCROLL_KEY);
    if (pending) {
      sessionStorage.removeItem(PENDING_SCROLL_KEY);

      if (navigationType === 'navigate') {
        window.requestAnimationFrame(() => {
          if (!scrollToSection(pending)) {
            handleInvalidHashScroll();
          } else if (NAV_SECTION_IDS.includes(pending)) {
            setActiveNavLink(pending);
          }
        });
      }
      return;
    }

    if (shouldStartHomepageAtTop()) return;

    const hash = window.location.hash.replace(/^#/, '');
    if (hash) {
      window.requestAnimationFrame(() => {
        if (scrollToSection(hash, 'auto')) {
          if (NAV_SECTION_IDS.includes(hash)) {
            setActiveNavLink(hash);
          }
        } else {
          handleInvalidHashScroll();
        }
      });
    }
  }

  function isHomepage() {
    return Boolean(
      document.querySelector('.gullyo-hero-section, .gullyo-bestsellers-section, #products.gullyo-product-grid')
    );
  }

  function closeMobileMenuDrawer() {
    const drawer = document.querySelector('header-drawer details.menu-drawer-container[open]');
    if (!drawer) return Promise.resolve();

    const component = drawer.closest('header-drawer');
    if (component && typeof component.close === 'function') {
      return component.close();
    }

    drawer.classList.remove('menu-open');
    drawer.open = false;
    drawer.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('scroll-lock');

    document.querySelectorAll('[data-gullyo-menu-inert]').forEach((element) => {
      element.inert = false;
      element.removeAttribute('data-gullyo-menu-inert');
    });

    return Promise.resolve();
  }

  let lastActiveNavSection = null;
  let activeNavRaf = null;

  function setActiveNavLink(sectionId) {
    if (!sectionId || sectionId === lastActiveNavSection) return;

    if (activeNavRaf) {
      cancelAnimationFrame(activeNavRaf);
    }

    activeNavRaf = requestAnimationFrame(() => {
      activeNavRaf = null;
      lastActiveNavSection = sectionId;

      document.querySelectorAll('.gullyo-nav-wave-link[data-gullyo-section]').forEach((link) => {
        const isActive = link.dataset.gullyoSection === sectionId;
        link.classList.toggle('is-active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    });
  }

  let activeSectionsInitialized = false;

  function initActiveSections() {
    if (activeSectionsInitialized) return;
    activeSectionsInitialized = true;

    let selectedSection = null;
    let observedSection = null;

    const hash = window.location.hash.replace(/^#/, '');
    if (NAV_SECTION_IDS.includes(hash)) {
      selectedSection = hash;
      setActiveNavLink(hash);
    } else if (!isHomepage()) {
      setActiveNavLink(DEFAULT_OFF_HOME_SECTION);
    }

    document.querySelectorAll('.gullyo-nav-wave-link[data-gullyo-section]').forEach((link) => {
      link.addEventListener('click', () => {
        const sectionId = link.dataset.gullyoSection;
        if (sectionId && NAV_SECTION_IDS.includes(sectionId)) {
          selectedSection = sectionId;
          setActiveNavLink(sectionId);
        }
      });
    });

    if (!isHomepage()) return;

    const elements = NAV_SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    if (!elements.length) return;

    const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        const nextSection = visible[0]?.target?.id;
        if (!nextSection || !NAV_SECTION_IDS.includes(nextSection)) return;
        if (nextSection === observedSection) return;

        observedSection = nextSection;
        if (!selectedSection || selectedSection === observedSection) {
          selectedSection = null;
          setActiveNavLink(observedSection);
        }
      },
      {
        rootMargin: isMobileViewport ? '-30% 0px -55% 0px' : '-35% 0px -50% 0px',
        threshold: isMobileViewport ? [0, 0.25] : [0, 0.15, 0.35, 0.55, 0.75],
      }
    );

    elements.forEach((element) => observer.observe(element));

    window.addEventListener('hashchange', () => {
      const next = window.location.hash.replace(/^#/, '');
      if (NAV_SECTION_IDS.includes(next)) {
        selectedSection = next;
        setActiveNavLink(next);
      }
    });
  }

  function initBestsellersCarousel() {
    document.querySelectorAll('[data-gullyo-carousel]').forEach((root) => {
      if (root.dataset.gullyoInitialized === 'true') return;
      root.dataset.gullyoInitialized = 'true';
      const track = root.querySelector('[data-gullyo-carousel-track]');
      const prev = root.querySelector('[data-gullyo-carousel-prev]');
      const next = root.querySelector('[data-gullyo-carousel-next]');
      if (!track || !prev || !next) return;

      const scrollStep = () => {
        const item = track.querySelector('[data-gullyo-carousel-item]');
        return item ? item.offsetWidth + 20 : 320;
      };

      prev.addEventListener('click', () => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.scrollBy({ left: -scrollStep(), behavior: reduced ? 'auto' : 'smooth' });
      });
      next.addEventListener('click', () => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.scrollBy({ left: scrollStep(), behavior: reduced ? 'auto' : 'smooth' });
      });
    });
  }

  const GULLYO_QUICK_ADD_LABEL = 'Quick Add';
  const GULLYO_ADDED_FEEDBACK_MS = 1500;
  const GULLYO_ERROR_FEEDBACK_MS = 1500;

  function getCartSectionIds() {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      if (component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });
    return Array.from(sectionIds);
  }

  function dispatchThemeCartAdd({ sourceId, itemCount, variantId, sections, didError, productId, sectionsHandled }) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: sourceId || '',
          data: {
            source: 'product-form-component',
            itemCount: itemCount || 0,
            variantId,
            sections,
            didError: Boolean(didError),
            productId,
            sectionsHandled: Boolean(sectionsHandled),
          },
        },
      })
    );
  }

  function ensureQuickAddStatus(form) {
    let status = form.querySelector('[data-gullyo-quick-add-status]');
    if (status) return status;

    status = document.createElement('span');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('data-gullyo-quick-add-status', '');
    form.appendChild(status);
    return status;
  }

  function setQuickAddButtonState(button, state, originalLabel) {
    if (!button) return;

    const labelNode = button.querySelector('.gullyo-product-card__quick-add-label');
    const label =
      originalLabel ||
      button.dataset.gullyoOriginalLabel ||
      labelNode?.textContent?.trim() ||
      GULLYO_QUICK_ADD_LABEL;
    if (!button.dataset.gullyoOriginalLabel) {
      button.dataset.gullyoOriginalLabel = label;
    }
    button.dataset.gullyoQuickAddState = state;

    if (state === 'loading') {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (labelNode) labelNode.textContent = 'Adding…';
      return;
    }

    button.removeAttribute('aria-busy');

    if (state === 'added') {
      button.disabled = true;
      if (labelNode) labelNode.textContent = label;
      return;
    }

    if (state === 'error') {
      button.disabled = false;
      if (labelNode) labelNode.textContent = 'Try again';
      return;
    }

    button.disabled = false;
    if (labelNode) labelNode.textContent = label;
  }

  async function handleGullyoQuickAddSubmit(form, event) {
    event.preventDefault();
    event.stopPropagation();

    if (form.dataset.gullyoCartProcessing === 'true') return;

    const variantInput = form.querySelector('[name="id"], [data-gullyo-variant-input]');
    const variantId = variantInput?.value?.trim();
    if (!variantId) {
      ensureQuickAddStatus(form).textContent = 'Select a variant before adding to bag.';
      return;
    }

    const button = form.querySelector('button[type="submit"]');
    const status = ensureQuickAddStatus(form);
    const originalLabel =
      button?.dataset.gullyoOriginalLabel ||
      button?.querySelector('.gullyo-product-card__quick-add-label')?.textContent?.trim() ||
      GULLYO_QUICK_ADD_LABEL;

    form.dataset.gullyoCartProcessing = 'true';
    window.GullyoCartSync?.beginCartAdd?.('quick-add');
    status.textContent = '';
    setQuickAddButtonState(button, 'loading', originalLabel);

    const flyVisual = window.GullyoFlyToCart?.begin({
      button,
      form,
      root: form.closest('[data-gullyo-product-card]'),
    });

    const formData = new FormData(form);
    formData.set('id', variantId);

    const sectionIds = getCartSectionIds();
    if (sectionIds.length) {
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const quantity = Number(formData.get('quantity')) || 1;

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        const message = data?.message || data?.description || 'Could not add to bag.';
        status.textContent = message;
        flyVisual?.cancel?.();
        dispatchThemeCartAdd({
          sourceId: form.id || variantId,
          itemCount: 0,
          variantId,
          didError: true,
        });
        setQuickAddButtonState(button, 'error', originalLabel);
        window.GullyoCartSync?.endCartAdd?.({ success: false });
        return;
      }

      if (data.sections && window.GullyoCartSync?.applyCartSectionsUpdate) {
        await window.GullyoCartSync.applyCartSectionsUpdate(data.sections);
      } else if (window.GullyoCartSync?.refreshCartSections) {
        await window.GullyoCartSync.refreshCartSections();
      }

      window.GullyoCartSync?.endCartAdd?.({ success: true });

      setQuickAddButtonState(button, 'added', originalLabel);
      status.textContent = 'Added to bag.';

      dispatchThemeCartAdd({
        sourceId: variantId,
        itemCount: quantity,
        variantId,
        sections: data.sections,
        sectionsHandled: true,
      });

      flyVisual?.finishOnSuccess?.();
    } catch (error) {
      flyVisual?.cancel?.();
      status.textContent = 'Network error. Please try again.';
      setQuickAddButtonState(button, 'error', originalLabel);
      window.GullyoCartSync?.endCartAdd?.({ success: false });
    } finally {
      if (button?.dataset.gullyoQuickAddState === 'added') {
        window.setTimeout(() => {
          form.dataset.gullyoCartProcessing = 'false';
          setQuickAddButtonState(button, 'idle', originalLabel);
        }, GULLYO_ADDED_FEEDBACK_MS);
      } else if (button?.dataset.gullyoQuickAddState === 'error') {
        window.setTimeout(() => {
          form.dataset.gullyoCartProcessing = 'false';
          setQuickAddButtonState(button, 'idle', originalLabel);
        }, GULLYO_ERROR_FEEDBACK_MS);
      } else {
        form.dataset.gullyoCartProcessing = 'false';
      }
    }
  }

  function bindGullyoQuickAddForm(form) {
    if (form.dataset.gullyoCartInitialized === 'true') return;
    form.dataset.gullyoCartInitialized = 'true';
    ensureQuickAddStatus(form);
  }

  function initQuickAdd() {
    if (window.__gullyoQuickAddInit) return;
    window.__gullyoQuickAddInit = true;

    document.querySelectorAll('.gullyo-product-card__quick-add-form').forEach(bindGullyoQuickAddForm);

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target.closest('.gullyo-product-card__quick-add-form');
        if (!form) return;
        handleGullyoQuickAddSubmit(form, event);
      },
      true
    );
  }

  function initSearchPanel() {
    if (window.__gullyoSearchPanelInit) return;

    const modal = document.getElementById('search-modal');
    if (!modal) return;

    const dialog =
      modal.querySelector('[data-gullyo-search-panel]') || modal.querySelector('dialog');
    const form = modal.querySelector('[data-gullyo-search-form]');
    const input = modal.querySelector('[data-gullyo-search-input]');
    const navigationStatus = modal.querySelector('[data-gullyo-search-navigation-status]');
    if (!dialog || !form || !input) return;

    window.__gullyoSearchPanelInit = true;

    let suppressOutsideCloseUntil = 0;
    /** @type {HTMLElement | null} */
    let lastSearchTrigger = null;

    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    if (!dialog.getAttribute('aria-labelledby')) {
      dialog.setAttribute('aria-label', 'Search GULLYO');
    }

    function isSearchOpen() {
      return Boolean(dialog.open);
    }

    function closeSearch() {
      if (!isSearchOpen()) return;
      dialog.removeAttribute('aria-busy');
      if (navigationStatus) navigationStatus.hidden = true;
      input.blur();
      if (typeof window.__gullyoSearchSuggestClear === 'function') {
        window.__gullyoSearchSuggestClear();
      }
      document.dispatchEvent(new CustomEvent('gullyo:search-closed'));
      if (typeof modal.closeDialog === 'function') {
        modal.closeDialog();
      } else {
        dialog.close();
      }

      focusWithoutScroll(lastSearchTrigger);
      lastSearchTrigger = null;
    }

    function isInsideSearchPanel(target) {
      if (!(target instanceof Element)) return false;
      return Boolean(
        target.closest('[data-gullyo-search-panel], [data-gullyo-search-form], [data-gullyo-search-suggestions]')
      );
    }

    function isSearchToggle(target) {
      if (!(target instanceof Element)) return false;
      return Boolean(target.closest('[data-gullyo-search-toggle], .search-action, search-button'));
    }

    form.addEventListener('submit', (event) => {
      const query = input.value.trim();
      if (!query) {
        event.preventDefault();
        return;
      }

      dialog.setAttribute('aria-busy', 'true');
      if (navigationStatus) navigationStatus.hidden = false;
    });

    modal.addEventListener('click', (event) => {
      const viewAll =
        event.target instanceof Element ? event.target.closest('.gullyo-search-suggest__view-all') : null;
      if (!(viewAll instanceof HTMLAnchorElement)) return;
      dialog.setAttribute('aria-busy', 'true');
      if (navigationStatus) navigationStatus.hidden = false;
    });

    dialog.addEventListener('toggle', () => {
      if (!dialog.open) {
        input.blur();
        return;
      }
      suppressOutsideCloseUntil = performance.now() + 120;
      window.requestAnimationFrame(() => focusWithoutScroll(input));
    });

    modal.addEventListener('dialog:open', () => {
      suppressOutsideCloseUntil = performance.now() + 120;
    });

    document.addEventListener('keydown', (event) => {
      if (!isSearchOpen() || event.key !== 'Escape') return;
      event.preventDefault();
      closeSearch();
    });

    document.addEventListener(
      'click',
      (event) => {
        if (!isSearchOpen()) return;
        if (performance.now() < suppressOutsideCloseUntil) return;

        const target = event.target;
        if (isInsideSearchPanel(target)) return;
        if (isSearchToggle(target)) return;

        closeSearch();
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        const toggle = event.target instanceof Element ? event.target.closest('[data-gullyo-search-toggle], .search-action, search-button') : null;
        if (toggle instanceof HTMLElement) {
          lastSearchTrigger = toggle;
        }
      },
      true
    );

    function syncSearchTriggerAccessibility() {
      document.querySelectorAll('.search-action').forEach((action) => {
        if (!(action instanceof HTMLElement)) return;

        const isHidden = window.getComputedStyle(action).display === 'none';
        action.toggleAttribute('aria-hidden', isHidden);
        if ('inert' in action) {
          action.inert = isHidden;
        }

        const button = action.querySelector('button');
        if (button instanceof HTMLButtonElement) {
          button.tabIndex = isHidden ? -1 : 0;
          button.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
        }
      });
    }

    syncSearchTriggerAccessibility();
    window.addEventListener('resize', syncSearchTriggerAccessibility);
    document.addEventListener('gullyo:menu-style-change', syncSearchTriggerAccessibility);
  }

  const GULLYO_ACCOUNT_SUBHEADING =
    'Sign in to view orders, manage your profile, and access future GULLYO drops.';

  const GULLYO_ACCOUNT_DIALOG_STYLES = `
    .account.loading::before {
      display: none !important;
      content: none !important;
    }

    .account.loading {
      min-height: 0 !important;
    }

    .account.loading slot[name="shop-login"] {
      visibility: visible !important;
    }

    .account__skeleton-line {
      background-color: rgba(0, 0, 0, 0.05) !important;
      animation: none !important;
      height: 14px !important;
    }

    .account__shortcuts--loading {
      opacity: 0.5;
    }

    .account__close.button.subdued {
      background-color: transparent !important;
      color: var(--gullyo-foreground, #141414) !important;
      border: none !important;
      box-shadow: none !important;
      width: 40px;
      height: 40px;
      min-width: 40px;
      min-height: 40px;
      padding: 0;
      margin: -4px -4px 0 0;
      border-radius: 8px;
    }

    .account__close.button.subdued:hover,
    .account__close.button.subdued:focus-visible {
      background-color: rgba(0, 0, 0, 0.04) !important;
      color: var(--gullyo-foreground, #141414) !important;
      box-shadow: none !important;
    }

    .account__header {
      background: #ffffff !important;
      padding: 1.5rem 1.5rem 0.75rem !important;
    }

    .account__heading {
      font-size: 1.25rem;
      letter-spacing: -0.02em;
      line-height: 1.2;
      margin: 0;
    }

    .gullyo-account-subheading {
      margin: 0.5rem 0 0;
      font-size: 0.875rem;
      line-height: 1.5;
      color: var(--gullyo-muted, #616161);
      font-family: var(--shopify-account-font-body, 'Inter', sans-serif);
      font-weight: 400;
      max-width: 100%;
    }

    .account__divider {
      gap: 0.75rem;
      margin-block: 0.25rem;
    }

    .account__divider-line {
      background-color: rgba(0, 0, 0, 0.08) !important;
      height: 1px;
    }

    .account__divider-text {
      font-family: var(--font-label-family, 'Archivo', sans-serif);
      font-size: 0.6875rem;
      letter-spacing: 0.14em;
      color: var(--gullyo-muted, #616161);
      text-transform: uppercase;
      font-weight: 500;
    }

    .account__login-slot {
      padding-inline: 0;
    }

    .account__menu-items {
      padding: 0.5rem 0 1rem;
    }

    .account__item-wrapper {
      padding-inline: 0;
    }

    .account__item {
      min-height: 52px;
      border: 1px solid rgba(0, 0, 0, 0.08);
      border-radius: 14px;
      margin-bottom: 0.5rem;
      transition: background-color 0.2s ease, border-color 0.2s ease;
    }

    .account__item:hover,
    .account__item:focus-visible {
      background-color: #fff9e8 !important;
      border-color: rgba(242, 194, 20, 0.25) !important;
    }

    .account__item-text {
      font-weight: 500;
    }

    slot[name="shop-login"]::slotted(*) {
      display: block;
      width: 100%;
      min-height: 48px;
    }

    .account__email,
    .account__name,
    .account__profile-email,
    .account__profile-name,
    [class*="profile"] [class*="email"],
    [class*="profile"] [class*="name"] {
      font-family: var(--shopify-account-font-body, 'Inter', sans-serif) !important;
      font-size: 0.875rem !important;
      line-height: 1.5 !important;
      color: var(--gullyo-foreground, #141414) !important;
    }

    .account__profile-label,
    [class*="profile"] [class*="label"] {
      font-family: var(--font-label-family, 'Archivo', sans-serif) !important;
      font-size: 0.6875rem !important;
      letter-spacing: 0.12em !important;
      text-transform: uppercase !important;
      color: var(--gullyo-muted, #616161) !important;
      font-weight: 600 !important;
    }
  `;

  const GULLYO_ACCOUNT_LOGIN_STYLES = `
    .account__login-form {
      gap: 1rem;
      padding-bottom: 0.25rem;
    }

    .account__email-input-wrapper {
      --input-height: 52px;
    }

    .account__email-input {
      border-color: rgba(0, 0, 0, 0.12) !important;
      color: var(--gullyo-foreground, #141414) !important;
      height: 52px;
      border-radius: 14px;
    }

    .account__email-input::placeholder {
      color: rgba(0, 0, 0, 0.42);
    }

    .account__email-input:focus-visible {
      border-color: var(--gullyo-gold, #f2c214) !important;
      box-shadow: 0 0 0 1px var(--gullyo-gold, #f2c214) !important;
      outline: none;
    }

    .account__email-submit.button.primary {
      background: transparent !important;
      color: var(--gullyo-foreground, #141414) !important;
      border: none !important;
      box-shadow: none !important;
      border-radius: 10px;
    }

    .account__email-submit.button.primary:hover,
    .account__email-submit.button.primary:focus-visible {
      background: rgba(0, 0, 0, 0.04) !important;
      color: var(--gullyo-foreground, #141414) !important;
    }

    .account__social-login-button.shop-login {
      border-radius: 14px !important;
      min-height: 48px;
    }
  `;

  const GULLYO_SHOP_LOGIN_STYLES = `
    :host {
      display: block;
      width: 100%;
      min-height: 48px;
    }
  `;

  function injectGullyoAccountStyles(shadowRoot, css, marker) {
    if (!shadowRoot || shadowRoot.querySelector(`[data-gullyo-account-style="${marker}"]`)) return;
    const style = document.createElement('style');
    style.setAttribute('data-gullyo-account-style', marker);
    style.textContent = css;
    shadowRoot.appendChild(style);
  }

  function isSignedOutAccountPanel(shadow) {
    return !shadow.querySelector('.account__email');
  }

  function applyGullyoAccountBranding(account) {
    const shadow = account.shadowRoot;
    if (!shadow) return;

    if (isSignedOutAccountPanel(shadow)) {
      const heading = shadow.querySelector('.account__heading');
      if (heading && heading.dataset.gullyoBranded !== 'true') {
        heading.textContent = 'GULLYO Account';
        heading.dataset.gullyoBranded = 'true';
      }

      const headerContent = shadow.querySelector('.account__header-content');
      if (headerContent && !headerContent.querySelector('[data-gullyo-subheading]')) {
        const sub = document.createElement('p');
        sub.className = 'gullyo-account-subheading';
        sub.setAttribute('data-gullyo-subheading', 'true');
        sub.textContent = GULLYO_ACCOUNT_SUBHEADING;
        heading?.insertAdjacentElement('afterend', sub);
      }
    }

    shadow.querySelectorAll('.account__item-text').forEach((label) => {
      if (label.dataset.gullyoLabelApplied === 'true') return;

      const item = label.closest('a, button');
      if (!item) return;

      const href = (item.getAttribute('href') || '').toLowerCase();
      const text = label.textContent.trim().toLowerCase();
      let nextLabel = '';

      if (href.includes('/orders') || text.includes('order')) {
        nextLabel = 'Orders';
      } else if (
        href.includes('/profile') ||
        (href.endsWith('/account') && !href.includes('orders')) ||
        text.includes('profile')
      ) {
        nextLabel = 'Profile';
      }

      if (nextLabel && label.textContent !== nextLabel) {
        label.textContent = nextLabel;
      }

      if (nextLabel) {
        label.dataset.gullyoLabelApplied = 'true';
      }
    });
  }

  function styleShopLogin(account) {
    const shadow = account.shadowRoot;
    if (!shadow) return;

    shadow.querySelectorAll('shop-login').forEach((shopLogin) => {
      if (shopLogin.shadowRoot) {
        injectGullyoAccountStyles(shopLogin.shadowRoot, GULLYO_SHOP_LOGIN_STYLES, 'shop-login');
      }
    });
  }

  function applyGullyoAccountPanelStyles(account) {
    const shadow = account.shadowRoot;
    if (!shadow) return;

    injectGullyoAccountStyles(shadow, GULLYO_ACCOUNT_DIALOG_STYLES, 'dialog');

    shadow.querySelectorAll('login-form').forEach((loginForm) => {
      if (loginForm.shadowRoot) {
        injectGullyoAccountStyles(loginForm.shadowRoot, GULLYO_ACCOUNT_LOGIN_STYLES, 'login');
      }
    });

    styleShopLogin(account);
    applyGullyoAccountBranding(account);
  }

  function initAccountPanel() {
    document.querySelectorAll('shopify-account').forEach((account) => {
      if (account.dataset.gullyoInitialized === 'true') return;
      account.dataset.gullyoInitialized = 'true';

      const refresh = () => {
        applyGullyoAccountPanelStyles(account);
      };

      const onOpen = () => {
        document.body.classList.add('gullyo-account-open');
        window.requestAnimationFrame(refresh);
        if (!window.matchMedia('(max-width: 749px)').matches) {
          window.setTimeout(refresh, 400);
        }
      };

      account.addEventListener('open', onOpen);
      account.addEventListener('close', () => {
        document.body.classList.remove('gullyo-account-open');
      });
      account.addEventListener('login-form:shop-login-loaded', refresh);
    });
  }

  function getVariantAvailabilityLabel(variantLike) {
    if (document.body?.classList.contains('gullyo-cart-localization-syncing')) {
      return 'Checking availability…';
    }

    if (!variantLike) return 'Unavailable in your region';

    const available =
      variantLike.available === true ||
      variantLike.available === 'true' ||
      variantLike.dataset?.variantAvailable === 'true';

    if (available) return '';

    if (variantLike.dataset?.availabilityLabel) {
      return variantLike.dataset.availabilityLabel;
    }

    const inventoryManagement =
      variantLike.inventory_management || variantLike.inventoryManagement;
    const inventoryPolicy = variantLike.inventory_policy || variantLike.inventoryPolicy;
    const inventoryQuantity = Number(
      variantLike.inventory_quantity ?? variantLike.inventoryQuantity ?? NaN
    );

    if (
      inventoryManagement === 'shopify' &&
      inventoryPolicy === 'deny' &&
      inventoryQuantity <= 0
    ) {
      return 'Sold out';
    }

    return 'Unavailable in your region';
  }

  function applySwatchSelection(card, swatch) {
    if (!card || !swatch) return;

    const image = card.querySelector('[data-gullyo-card-image]');
    const links = card.querySelectorAll('[data-gullyo-card-link]');
    const label = card.querySelector('[data-gullyo-variant-label]');
    const price = card.querySelector('[data-gullyo-card-price]');
    const variantInput = card.querySelector('[data-gullyo-variant-input]');
    const quickAdd = card.querySelector('[data-gullyo-quick-add]');
    const soldOutBadge = card.querySelector('[data-gullyo-sold-out-badge]');
    const swatches = card.querySelectorAll('[data-gullyo-swatch]');

    const imageSrc = swatch.dataset.imageSrc;
    const imageSrcset = swatch.dataset.imageSrcset;
    if (image && imageSrc) {
      image.src = imageSrc;
      if (imageSrcset) {
        image.srcset = imageSrcset;
      } else {
        image.removeAttribute('srcset');
      }
    }

    if (links.length && swatch.dataset.variantUrl) {
      links.forEach((link) => {
        link.href = swatch.dataset.variantUrl;
      });
    }

    const variantTitle = swatch.dataset.variantLabel || swatch.dataset.variantTitle;
    if (label && variantTitle) {
      label.textContent = variantTitle.toLocaleUpperCase();
    }

    if (price && swatch.dataset.variantPrice) {
      price.textContent = swatch.dataset.variantPrice;
    }

    if (variantInput && swatch.dataset.variantId) {
      variantInput.value = swatch.dataset.variantId;
    }

    const isAvailable = swatch.dataset.variantAvailable === 'true';
    if (quickAdd) {
      quickAdd.classList.toggle('hidden', !isAvailable);
      const quickAddButton = quickAdd.querySelector('button[type="submit"]');
      if (quickAddButton instanceof HTMLButtonElement) {
        quickAddButton.disabled = !isAvailable;
      }
    }
    if (soldOutBadge) {
      soldOutBadge.classList.toggle('hidden', isAvailable);
      if (!isAvailable) {
        soldOutBadge.textContent = getVariantAvailabilityLabel(swatch);
        soldOutBadge.dataset.availabilityState = swatch.dataset.availabilityState || 'unavailable';
      }
    }

    swatches.forEach((hit) => {
      const selected = hit === swatch;
      hit.classList.toggle('is-selected', selected);
      hit.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }

  function prioritizeSelectedCardSwatch(card) {
    if (!(card instanceof Element)) return;

    const row = card.querySelector('[data-gullyo-swatches]');
    const selected = row?.querySelector('[data-gullyo-swatch][aria-pressed="true"]');
    const first = row?.querySelector('[data-gullyo-swatch]');
    if (!row || !selected || !first || selected === first) return;

    row.insertBefore(selected, first);
  }

  let productCardSwatchBound = false;

  function updateProductCardSwatchOverflow(row) {
    if (!(row instanceof HTMLElement)) return;
    const maxScrollLeft = Math.max(0, row.scrollWidth - row.clientWidth);
    const overflowing = maxScrollLeft > 2;
    row.dataset.overflowStart = overflowing && row.scrollLeft > 2 ? 'true' : 'false';
    row.dataset.overflowEnd = overflowing && row.scrollLeft < maxScrollLeft - 2 ? 'true' : 'false';
  }

  function bindProductCardSwatchOverflow(row) {
    if (!(row instanceof HTMLElement) || row.dataset.gullyoOverflowBound === 'true') return;
    row.dataset.gullyoOverflowBound = 'true';
    row.addEventListener('scroll', () => updateProductCardSwatchOverflow(row), { passive: true });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => updateProductCardSwatchOverflow(row));
      observer.observe(row);
    }

    updateProductCardSwatchOverflow(row);
  }

  function bindProductCardSwatchesWithin(root = document) {
    if (!(root instanceof Document || root instanceof Element)) return;
    root.querySelectorAll('[data-gullyo-product-card]').forEach(prioritizeSelectedCardSwatch);
    if (root instanceof Element && root.matches('[data-gullyo-product-card]')) {
      prioritizeSelectedCardSwatch(root);
    }
    root.querySelectorAll('[data-gullyo-swatches]').forEach(bindProductCardSwatchOverflow);
    if (root instanceof Element && root.matches('[data-gullyo-swatches]')) {
      bindProductCardSwatchOverflow(root);
    }
  }

  function initProductCardSwatches() {
    if (productCardSwatchBound) return;
    productCardSwatchBound = true;
    bindProductCardSwatchesWithin();

    const swatchObserver = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) bindProductCardSwatchesWithin(node);
        });
      });
    });
    swatchObserver.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('resize', () => {
      document.querySelectorAll('[data-gullyo-swatches]').forEach(updateProductCardSwatchOverflow);
    });

    document.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-gullyo-swatch]');
      if (!swatch) return;

      event.preventDefault();
      event.stopPropagation();

      const card = swatch.closest('[data-gullyo-product-card]');
      applySwatchSelection(card, swatch);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const swatch = event.target.closest('[data-gullyo-swatch]');
      if (!swatch) return;

      event.preventDefault();
      const card = swatch.closest('[data-gullyo-product-card]');
      applySwatchSelection(card, swatch);
    });
  }

  function getPdpEyebrowLabel(section) {
    const type = (section?.dataset.productType || '').trim();
    if (type && type.toLowerCase() !== 'sunglasses') {
      return type.toUpperCase();
    }

    const vendor = (section?.dataset.productVendor || '').trim();
    if (vendor) {
      return vendor.toUpperCase();
    }

    return 'EYEWEAR';
  }

  function updatePdpEyebrow(section) {
    if (!section || section.querySelector('.gullyo-pdp__eyebrow')) return;

    const title =
      section.querySelector('.product-details h1')
      || section.querySelector('.product-details [class*="product-title"] h1')
      || section.querySelector('.product-details [class*="product-title"] p');

    if (!title) return;

    const eyebrow = document.createElement('p');
    eyebrow.className = 'gullyo-pdp__eyebrow';
    eyebrow.textContent = getPdpEyebrowLabel(section);
    title.insertAdjacentElement('beforebegin', eyebrow);
  }

  function getInitialPdpVariant(section) {
    const script = section?.querySelector('variant-picker script[type="application/json"]');
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  function updatePdpVariantMeta(section, variant) {
    if (!section || !variant) return;

    let label = section.querySelector('.gullyo-pdp__variant-label');
    const title = section.querySelector('.product-details h1, .product-details [class*="product-title"] h1, .product-details [class*="product-title"] p');
    if (!label && title) {
      label = document.createElement('p');
      label.className = 'gullyo-pdp__variant-label';
      title.insertAdjacentElement('afterend', label);
    }

    const swatchLegend = section.querySelector('.variant-option__swatch-value');
    const legendText = swatchLegend?.textContent?.trim();
    const variantTitle = (legendText || variant.public_title || variant.title || variant.name || '').trim();

    if (label && variantTitle) {
      label.textContent = variantTitle.toLocaleUpperCase();
    }

    let stock = section.querySelector('.gullyo-pdp__stock');
    const price = section.querySelector('.product-details .price');
    if (!stock && price) {
      stock = document.createElement('p');
      stock.className = 'gullyo-pdp__stock';
      price.insertAdjacentElement('afterend', stock);
    }

    if (stock) {
      const available = Boolean(variant.available);
      if (available) {
        stock.textContent = 'In stock';
      } else {
        stock.textContent = getVariantAvailabilityLabel(variant);
      }
      stock.classList.toggle('gullyo-pdp__stock--in', available);
      stock.classList.toggle('gullyo-pdp__stock--out', !available);
      stock.classList.toggle('gullyo-pdp__stock--unavailable', !available && stock.textContent !== 'Sold out');
    }

    const infoVariant = section.querySelector('[data-gullyo-product-info-variant]');
    if (infoVariant) {
      const infoTitle = (legendText || variant.public_title || variant.title || variant.name || '').trim();
      if (infoTitle && infoTitle !== 'Default Title') {
        infoVariant.textContent = infoTitle.toLocaleUpperCase();
      }
    }
  }

  function initPdpTrust(section) {
    if (!section || !section.matches('.product-information')) return;
    const buyButtons = section.querySelector('.buy-buttons-block, buy-buttons-component');
    if (!buyButtons || section.querySelector('.gullyo-pdp-trust')) return;

    const trust = document.createElement('div');
    trust.className = 'gullyo-pdp-trust';
    trust.textContent = 'Free standard EU delivery · 21-day returns on unused items';
    buyButtons.insertAdjacentElement('afterend', trust);
  }

  function normalizeVariantKey(text) {
    if (!text) return '';
    let value = String(text).toLowerCase().split('.')[0];
    value = value.replace(/[—–/_-]/g, ' ');
    value = value.replace(/\bgrey\b/g, 'gray');
    const tokens = [
      'front',
      'left',
      'right',
      'side',
      'angle',
      'angled',
      'back',
      'center',
      'main',
      'model',
      'hero',
      'lens',
      'lenses',
    ];
    for (const token of tokens) {
      value = value.replace(new RegExp(`\\b${token}\\b`, 'g'), ' ');
    }
    return value.replace(/\s+/g, ' ').trim();
  }

  function variantKeysMatch(mediaKey, variantKey) {
    if (!mediaKey || !variantKey) return false;
    if (mediaKey === variantKey) return true;
    const prefix = mediaKey.slice(0, variantKey.length);
    const next = mediaKey.slice(variantKey.length, variantKey.length + 1);
    return prefix === variantKey && (next === ' ' || next === '');
  }

  function getVariantKeyFromResource(variant) {
    return normalizeVariantKey(variant?.public_title || variant?.title || variant?.name || '');
  }

  const GULLYO_VIEWER_TRANSITION_MS = 170;
  let gullyoImageViewerKeyboardHandler = null;

  function getViewerDecodeCache(gallery) {
    if (!gallery.__gullyoViewerDecodeCache) {
      gallery.__gullyoViewerDecodeCache = new Map();
    }
    return gallery.__gullyoViewerDecodeCache;
  }

  function clearViewerDecodeCache(gallery) {
    gallery.__gullyoViewerDecodeCache?.clear();
  }

  function getCurrentPdpImageSource(gallery, mediaId) {
    let slide = null;

    if (mediaId) {
      slide = gallery.querySelector(`slideshow-slide[data-gullyo-media-id="${mediaId}"]:not([hidden])`);
      if (slide?.getAttribute('aria-hidden') === 'true') {
        slide = null;
      }
    }

    if (!slide) {
      const slideshow = gallery.querySelector('slideshow-component');
      slide = getVisibleSlideFromSlideshow(slideshow, getGallerySlides(gallery));
    }

    const img = slide?.querySelector('img.product-media__image');
    if (!img?.src) return null;

    return {
      src: img.currentSrc || img.src,
      srcset: img.getAttribute('srcset') || '',
      sizes: img.getAttribute('sizes') || '',
    };
  }

  function applyViewerImageSources(imageEl, entry, fallback) {
    if (!imageEl) return;

    if (fallback?.src) {
      imageEl.src = fallback.src;
      if (fallback.srcset) imageEl.srcset = fallback.srcset;
      else imageEl.removeAttribute('srcset');
      if (fallback.sizes) imageEl.sizes = fallback.sizes;
      else imageEl.removeAttribute('sizes');
    } else if (entry) {
      imageEl.src = entry.displaySrc;
      if (entry.displaySrcset) imageEl.srcset = entry.displaySrcset;
      else imageEl.removeAttribute('srcset');
      if (entry.displaySizes) imageEl.sizes = entry.displaySizes;
      else imageEl.removeAttribute('sizes');
    }

    if (entry?.alt) imageEl.alt = entry.alt;
    if (entry?.width) imageEl.width = entry.width;
    if (entry?.height) imageEl.height = entry.height;
  }

  function viewerSourcesMatch(imageEl, entry) {
    if (!imageEl || !entry?.displaySrc) return false;
    const current = imageEl.currentSrc || imageEl.src || '';
    return current === entry.displaySrc || current.startsWith(`${entry.displaySrc.split('?')[0]}`);
  }

  function getOpenGullyoImageViewer() {
    return document.querySelector('.product-information .gullyo-image-viewer[open]');
  }

  function isViewerThumbnailAvailable(thumbnail) {
    if (!thumbnail) return false;
    if (thumbnail.hidden) return false;
    if (thumbnail.getAttribute('aria-hidden') === 'true') return false;
    if (thumbnail.offsetParent === null) return false;
    return true;
  }

  function getOrderedViewerImages(gallery) {
    const seen = new Set();

    return Array.from(gallery.querySelectorAll('[data-gullyo-viewer-thumbnail]'))
      .filter(isViewerThumbnailAvailable)
      .map((thumbnail) => {
        const id = thumbnail.dataset.mediaId || '';
        if (!id || seen.has(id)) return null;
        seen.add(id);

        const thumbImg = thumbnail.querySelector('img');

        return {
          id,
          displaySrc:
            thumbnail.dataset.displaySrc || thumbnail.dataset.fullSrc || '',
          displaySrcset:
            thumbnail.dataset.displaySrcset || thumbnail.dataset.fullSrcset || '',
          displaySizes: thumbnail.dataset.displaySizes || '',
          alt: thumbnail.dataset.alt || thumbImg?.alt || '',
          width: parseInt(thumbnail.dataset.width, 10) || 0,
          height: parseInt(thumbnail.dataset.height, 10) || 0,
          thumbnailSrc:
            thumbnail.dataset.thumbnailSrc || thumbImg?.currentSrc || thumbImg?.src || '',
        };
      })
      .filter((entry) => entry && entry.id && entry.displaySrc);
  }

  function getActiveViewerMediaId(gallery) {
    const slideshow = gallery.querySelector('slideshow-component');
    const slides = getGallerySlides(gallery);
    const currentSlide = getVisibleSlideFromSlideshow(slideshow, slides);
    if (currentSlide?.dataset.gullyoMediaId) {
      return currentSlide.dataset.gullyoMediaId;
    }

    const activeThumb = gallery.querySelector(
      '[data-gullyo-viewer-thumbnail][aria-current="true"]'
    );
    return activeThumb?.dataset.mediaId || '';
  }

  function getActiveMainMediaTrigger(gallery) {
    const triggers = gallery.querySelectorAll('[data-gullyo-main-media-trigger]');
    for (const trigger of triggers) {
      if (isMediaItemVisible(trigger)) return trigger;
    }
    return null;
  }

  function getViewerIndexForMediaId(gallery, media, mediaId) {
    if (!mediaId) return 0;
    const index = media.findIndex((entry) => String(entry.id) === String(mediaId));
    return index >= 0 ? index : 0;
  }

  async function preloadViewerImage(gallery, image) {
    if (!image?.displaySrc) return null;

    const cache = getViewerDecodeCache(gallery);
    if (cache.has(image.displaySrc)) {
      return cache.get(image.displaySrc);
    }

    const promise = new Promise((resolve) => {
      const preload = new Image();

      if (image.displaySrcset) {
        preload.srcset = image.displaySrcset;
        if (image.displaySizes) preload.sizes = image.displaySizes;
      }

      preload.src = image.displaySrc;
      preload.decoding = 'async';

      const complete = async () => {
        try {
          if (typeof preload.decode === 'function') {
            await preload.decode();
          }
        } catch {
          /* fall back to the completed load */
        }
        resolve(preload);
      };

      if (preload.complete) {
        complete();
      } else {
        preload.addEventListener('load', complete, { once: true });
        preload.addEventListener('error', () => resolve(null), { once: true });
      }
    });

    cache.set(image.displaySrc, promise);
    return promise;
  }

  function createGullyoImageViewerController(section, gallery, dialog) {
    const refs = {
      counter: dialog.querySelector('[data-gullyo-image-viewer-counter]'),
      close: dialog.querySelector('[data-gullyo-image-viewer-close]'),
      prev: dialog.querySelector('[data-gullyo-image-viewer-prev]'),
      next: dialog.querySelector('[data-gullyo-image-viewer-next]'),
      image: dialog.querySelector('[data-gullyo-image-viewer-image]'),
      thumbnails: dialog.querySelector('[data-gullyo-image-viewer-thumbnails]'),
      stage: dialog.querySelector('.gullyo-image-viewer__stage'),
    };

    const state = {
      media: [],
      activeIndex: 0,
      switchToken: 0,
      lastTrigger: null,
      previousScrollY: 0,
      bodyLocked: false,
    };

    function normalizeIndex(index) {
      if (!state.media.length) return 0;
      return ((index % state.media.length) + state.media.length) % state.media.length;
    }

    function updateCounter() {
      if (!refs.counter) return;
      refs.counter.textContent = state.media.length ? `${state.activeIndex + 1} / ${state.media.length}` : '';
    }

    function updateMediaCountState() {
      dialog.dataset.mediaCount = String(state.media.length || 0);
    }

    function updateNavVisibility() {
      const single = state.media.length <= 1;
      [refs.prev, refs.next].forEach((button) => {
        if (!button) return;
        button.hidden = single;
        button.disabled = single;
        button.setAttribute('aria-hidden', single ? 'true' : 'false');
      });
      updateMediaCountState();
    }

    function syncThumbnailUI() {
      if (!refs.thumbnails) return;

      refs.thumbnails.querySelectorAll('[data-gullyo-image-viewer-thumb]').forEach((button, index) => {
        const selected = index === state.activeIndex;
        button.setAttribute('aria-current', selected ? 'true' : 'false');
        if (selected) {
          button.scrollIntoView({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      });
    }

    function renderThumbnails() {
      if (!refs.thumbnails) return;

      refs.thumbnails.replaceChildren();

      if (state.media.length <= 1) {
        refs.thumbnails.hidden = true;
        return;
      }

      refs.thumbnails.hidden = false;

      state.media.forEach((entry, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'gullyo-image-viewer__thumb';
        button.dataset.gullyoImageViewerThumb = String(index);
        button.setAttribute('aria-label', entry.alt || `Image ${index + 1}`);
        button.setAttribute('aria-current', index === state.activeIndex ? 'true' : 'false');

        const thumb = document.createElement('img');
        thumb.src = entry.thumbnailSrc;
        thumb.alt = '';
        thumb.loading = 'lazy';
        thumb.decoding = 'async';
        thumb.width = 160;
        thumb.height =
          entry.width > 0 && entry.height > 0
            ? Math.max(1, Math.round((160 * entry.height) / entry.width))
            : 160;
        button.appendChild(thumb);

        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          setActiveViewerImage(index);
        });

        refs.thumbnails.appendChild(button);
      });
    }

    function refreshMediaList() {
      state.media = getOrderedViewerImages(gallery);
      renderThumbnails();
      updateNavVisibility();
      updateCounter();
      updateMediaCountState();
    }

    async function setActiveViewerImage(requestedIndex, options = {}) {
      if (!state.media.length || !refs.image) return;

      const requestId = ++state.switchToken;
      const index = normalizeIndex(requestedIndex);
      const entry = state.media[index];
      if (!entry) return;

      const prevEntry = state.media[normalizeIndex(index - 1)];
      const nextEntry = state.media[normalizeIndex(index + 1)];
      preloadViewerImage(gallery, prevEntry);
      preloadViewerImage(gallery, nextEntry);

      const pdpSource =
        options.usePdpFallback === false ? null : getCurrentPdpImageSource(gallery, entry.id);
      const shouldUpgrade = !viewerSourcesMatch(refs.image, entry);
      const canShowPdpImmediately = Boolean(pdpSource?.src && (options.immediatePdp || shouldUpgrade));

      if (canShowPdpImmediately && (!refs.image.src || options.immediatePdp)) {
        applyViewerImageSources(refs.image, null, pdpSource);
        refs.image.style.transition = 'none';
        refs.image.style.opacity = '1';
      }

      if (!shouldUpgrade && state.activeIndex === index && refs.image.src) {
        state.activeIndex = index;
        updateCounter();
        syncThumbnailUI();
        updateNavVisibility();
        return;
      }

      const decoded = await preloadViewerImage(gallery, entry);
      if (requestId !== state.switchToken) return;

      const shouldCrossfade =
        !prefersReducedMotion() &&
        options.animate !== false &&
        refs.image.src &&
        shouldUpgrade;

      if (shouldCrossfade) {
        refs.image.style.transition = `opacity ${GULLYO_VIEWER_TRANSITION_MS}ms ease`;
        refs.image.style.opacity = '0.92';
        await new Promise((resolve) => {
          window.setTimeout(resolve, Math.round(GULLYO_VIEWER_TRANSITION_MS / 2));
        });
        if (requestId !== state.switchToken) return;
      }

      applyViewerImageSources(refs.image, entry);

      if (decoded && typeof decoded.decode === 'function') {
        try {
          await decoded.decode();
        } catch {
          /* decoded preload is enough to paint */
        }
      }
      if (requestId !== state.switchToken) return;

      state.activeIndex = index;
      updateCounter();
      syncThumbnailUI();
      updateNavVisibility();

      if (shouldCrossfade) {
        refs.image.style.opacity = '1';
      } else {
        refs.image.style.transition = 'none';
        refs.image.style.opacity = '1';
      }
    }

    function lockBodyScroll() {
      state.previousScrollY = window.scrollY;
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${state.previousScrollY}px`;
      state.bodyLocked = true;
    }

    function unlockBodyScroll() {
      if (!state.bodyLocked) return;
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: state.previousScrollY, behavior: 'instant' });
      state.bodyLocked = false;
    }

    function bindViewerKeyboard() {
      if (gullyoImageViewerKeyboardHandler) return;

      gullyoImageViewerKeyboardHandler = (event) => {
        if (!dialog.open) return;

        switch (event.key) {
          case 'Escape':
            event.preventDefault();
            close();
            break;
          case 'ArrowLeft':
            if (state.media.length <= 1) return;
            event.preventDefault();
            setActiveViewerImage(state.activeIndex - 1);
            break;
          case 'ArrowRight':
            if (state.media.length <= 1) return;
            event.preventDefault();
            setActiveViewerImage(state.activeIndex + 1);
            break;
          case 'Home':
            if (state.media.length <= 1) return;
            event.preventDefault();
            setActiveViewerImage(0);
            break;
          case 'End':
            if (state.media.length <= 1) return;
            event.preventDefault();
            setActiveViewerImage(state.media.length - 1);
            break;
          default:
            break;
        }
      };

      document.addEventListener('keydown', gullyoImageViewerKeyboardHandler);
    }

    function unbindViewerKeyboard() {
      if (!gullyoImageViewerKeyboardHandler) return;
      document.removeEventListener('keydown', gullyoImageViewerKeyboardHandler);
      gullyoImageViewerKeyboardHandler = null;
    }

    function resolveOpenIndex(activeMediaId) {
      if (activeMediaId) {
        const found = state.media.findIndex((entry) => String(entry.id) === String(activeMediaId));
        if (found >= 0) return found;
      }
      return getViewerIndexForMediaId(gallery, state.media, getActiveViewerMediaId(gallery));
    }

    function showViewerDialog() {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    function open(activeMediaIdOrTrigger) {
      let trigger = null;
      let activeMediaId = null;

      if (activeMediaIdOrTrigger instanceof Element) {
        trigger = activeMediaIdOrTrigger;
        activeMediaId = trigger.dataset.mediaId || trigger.dataset.gullyoMediaId || null;
      } else if (activeMediaIdOrTrigger != null && activeMediaIdOrTrigger !== '') {
        activeMediaId = String(activeMediaIdOrTrigger);
        trigger =
          gallery.querySelector(`[data-gullyo-main-media-trigger][data-media-id="${activeMediaId}"]`) ||
          gallery.querySelector(`[data-gullyo-main-media-trigger][data-gullyo-media-id="${activeMediaId}"]`);
      }

      state.lastTrigger = trigger || getActiveMainMediaTrigger(gallery);
      refreshMediaList();
      if (!state.media.length) return;

      lockBodyScroll();
      showViewerDialog();
      setActiveViewerImage(resolveOpenIndex(activeMediaId), {
        animate: false,
        immediatePdp: true,
      });
      bindViewerKeyboard();
      focusWithoutScroll(refs.close);
    }

    function close() {
      state.switchToken += 1;
      unbindViewerKeyboard();
      dialog.close();
      unlockBodyScroll();
      focusWithoutScroll(state.lastTrigger || getActiveMainMediaTrigger(gallery));
    }

    refs.close?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      close();
    });

    refs.prev?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setActiveViewerImage(state.activeIndex - 1);
    });

    refs.next?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setActiveViewerImage(state.activeIndex + 1);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });

    let startX = 0;
    let startY = 0;
    let wheelAccum = 0;
    let wheelLocked = false;
    let wheelUnlockTimer = null;
    const swipeTarget = refs.stage || dialog;
    const VIEWER_WHEEL_THRESHOLD = 48;
    const VIEWER_WHEEL_COOLDOWN_MS = 280;

    swipeTarget.addEventListener(
      'wheel',
      (event) => {
        if (!dialog.open || state.media.length <= 1) return;
        if (event.target.closest('[data-gullyo-image-viewer-thumbnails]')) return;

        const dx = event.deltaX;
        const dy = event.deltaY;
        if (Math.abs(dx) <= Math.abs(dy)) return;

        event.preventDefault();
        if (wheelLocked) return;

        wheelAccum += dx;
        if (Math.abs(wheelAccum) < VIEWER_WHEEL_THRESHOLD) return;

        wheelLocked = true;
        wheelAccum = 0;
        setActiveViewerImage(state.activeIndex + (dx > 0 ? 1 : -1));

        if (wheelUnlockTimer) window.clearTimeout(wheelUnlockTimer);
        wheelUnlockTimer = window.setTimeout(() => {
          wheelLocked = false;
          wheelAccum = 0;
        }, VIEWER_WHEEL_COOLDOWN_MS);
      },
      { passive: false }
    );

    swipeTarget.addEventListener(
      'touchstart',
      (event) => {
        if (!dialog.open || event.touches.length !== 1) return;
        if (event.target.closest('[data-gullyo-image-viewer-thumbnails]')) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );

    swipeTarget.addEventListener(
      'touchend',
      (event) => {
        if (!dialog.open || event.changedTouches.length !== 1) return;
        if (event.target.closest('[data-gullyo-image-viewer-thumbnails]')) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        if (state.media.length <= 1) return;
        if (Math.abs(dx) < GULLYO_SWIPE_MIN || Math.abs(dy) > Math.abs(dx)) return;
        setActiveViewerImage(state.activeIndex + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );

    return {
      open,
      close,
      setActive: (index) => setActiveViewerImage(index),
      next: () => setActiveViewerImage(state.activeIndex + 1),
      previous: () => setActiveViewerImage(state.activeIndex - 1),
      refreshMedia: refreshMediaList,
      setActiveViewerImage,
      getState: () => state,
    };
  }

  function isMainMediaViewerClick(event, gallery, trigger) {
    if (!trigger || !gallery.contains(trigger)) return false;
    if (!isMediaItemVisible(trigger)) return false;
    if (event.target.closest('.slideshow-control--previous, .slideshow-control--next')) return false;
    if (event.target.closest('slideshow-controls, .slideshow-controls__thumbnail')) return false;
    if (event.target.closest('button, a[href]') && !event.target.closest('[data-gullyo-main-media-trigger]')) {
      return false;
    }
    return true;
  }

  function initGullyoImageViewer(section, gallery) {
    const dialog = gallery.querySelector('.gullyo-image-viewer[data-gullyo-image-viewer]');
    if (!dialog || dialog.dataset.gullyoImageViewerBound) return;

    dialog.dataset.gullyoImageViewerBound = 'true';
    const controller = createGullyoImageViewerController(section, gallery, dialog);
    gallery.__gullyoImageViewer = controller;

    gallery.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest('[data-gullyo-main-media-trigger]');
        if (!isMainMediaViewerClick(event, gallery, trigger)) return;

        event.preventDefault();
        event.stopPropagation();
        controller.open(trigger);
      },
      { capture: true }
    );
  }

  function refreshGullyoImageViewer(gallery) {
    const controller = gallery.__gullyoImageViewer;
    if (!controller) return;

    clearViewerDecodeCache(gallery);
    controller.refreshMedia();

    if (gallery.querySelector('.gullyo-image-viewer[open]')) {
      const media = controller.getState().media;
      const activeIndex = getViewerIndexForMediaId(gallery, media, getActiveViewerMediaId(gallery));
      controller.setActiveViewerImage(activeIndex, { animate: false, immediatePdp: true });
    }
  }

  const GULLYO_SWIPE_MIN = 44;
  const GULLYO_SELECTION_LOCK_MS = 520;
  let pdpGalleryKeyboardBound = false;

  function isKeyboardTargetIgnored(target) {
    if (!target || !(target instanceof Element)) return true;

    const tag = target.tagName.toLowerCase();
    if (target.isContentEditable || target.closest('[contenteditable="true"]')) return true;
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    if (
      target.closest(
        'quantity-selector-component, .quantity-selector, #search-modal, .search-modal, cart-drawer-component dialog[open], #gullyo-account-drawer-dialog[open], [data-gullyo-search-panel], [data-gullyo-account-panel]'
      )
    ) {
      return true;
    }

    return false;
  }

  function isElementInViewport(element) {
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function dedupeOrderedIndicesByMediaId(indices, elements) {
    const seen = new Set();
    return indices.filter((index) => {
      const id = elements[index]?.dataset?.gullyoMediaId;
      if (!id) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }

  function isMediaItemVisible(item) {
    if (!item) return false;
    if (item.hidden) return false;
    if (item.getAttribute('aria-hidden') === 'true') return false;
    if (item.style.display === 'none') return false;
    return true;
  }

  function isVariantVisibleSlide(slide) {
    if (!slide) return false;
    if (slide.hidden) return false;
    if (slide.style.display === 'none') return false;
    return true;
  }

  function getGallerySlides(gallery) {
    return Array.from(gallery.querySelectorAll('slideshow-slide[data-gullyo-media-item]'));
  }

  function getVisibleOrderedMedia(gallery) {
    return getGallerySlides(gallery).filter(isVariantVisibleSlide);
  }

  function getVisibleOrderedMediaIds(gallery) {
    return getVisibleOrderedMedia(gallery)
      .map((slide) => slide.dataset.gullyoMediaId)
      .filter(Boolean);
  }

  function getThumbnailControls(gallery) {
    return (
      gallery.querySelector('slideshow-controls[thumbnails].media-gallery__mobile-controls') ||
      gallery.querySelector('slideshow-controls[thumbnails]:not(.media-gallery__mobile-controls)') ||
      gallery.querySelector('slideshow-controls[thumbnails]')
    );
  }

  function getThumbnailButtons(gallery) {
    const controls = getThumbnailControls(gallery);
    return controls ? Array.from(controls.querySelectorAll('.slideshow-controls__thumbnail')) : [];
  }

  function scrollThumbnailIntoStrip(thumb, behavior) {
    if (!thumb) return;

    const strip = thumb.closest('.slideshow-controls__thumbnails');
    const resolvedBehavior = behavior || (prefersReducedMotion() ? 'auto' : 'smooth');

    if (strip instanceof HTMLElement && strip.scrollWidth > strip.clientWidth) {
      const centerActiveThumbnail = (scrollBehavior) => {
        const stripRect = strip.getBoundingClientRect();
        const thumbRect = thumb.getBoundingClientRect();
        const maxLeft = Math.max(0, strip.scrollWidth - strip.clientWidth);
        const targetLeft =
          strip.scrollLeft +
          (thumbRect.left - stripRect.left) -
          (strip.clientWidth - thumbRect.width) / 2;

        strip.scrollTo({
          left: Math.min(maxLeft, Math.max(0, targetLeft)),
          behavior: scrollBehavior,
        });
      };

      centerActiveThumbnail(resolvedBehavior);

      // Slideshow selection can finish after the first scroll request on touch devices.
      // Re-center the still-active thumbnail once the selection/layout cycle has settled.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!thumb.isConnected || thumb.getAttribute('aria-current') !== 'true') return;
          centerActiveThumbnail('auto');
        });
      });
      return;
    }

    thumb.scrollIntoView({
      behavior: resolvedBehavior,
      block: 'nearest',
      inline: 'center',
    });
  }

  function getAbsoluteIndexForMediaId(gallery, slides, mediaId) {
    if (!mediaId) return -1;
    return slides.findIndex((slide) => String(slide.dataset.gullyoMediaId) === String(mediaId));
  }

  function getPrimaryThumbnailButtons(gallery) {
    return getThumbnailButtons(gallery);
  }

  function buildGalleryOrderedIndices(gallery, slides) {
    const visibleMediaIds = getVisibleOrderedMediaIds(gallery);
    if (visibleMediaIds.length) {
      return visibleMediaIds
        .map((mediaId) => getAbsoluteIndexForMediaId(gallery, slides, mediaId))
        .filter((index) => index >= 0);
    }

    const thumbs = getThumbnailButtons(gallery);
    const indices = [];

    thumbs.forEach((thumb, thumbIndex) => {
      if (!isMediaItemVisible(thumb)) return;
      const slide = slides[thumbIndex];
      if (!slide || !isVariantVisibleSlide(slide)) return;
      indices.push(thumbIndex);
    });

    return dedupeOrderedIndicesByMediaId(indices, slides);
  }

  function cacheGalleryOrderedIndices(gallery, slides) {
    const ordered = buildGalleryOrderedIndices(gallery, slides);
    gallery.__gullyoOrderedIndices = ordered;
    return ordered;
  }

  function getGalleryOrderedIndices(gallery) {
    return gallery.__gullyoOrderedIndices || [];
  }

  function getVisibleSlideFromSlideshow(slideshow, slides) {
    const currentSlide = slideshow?.slides?.[slideshow.current];
    if (currentSlide && !currentSlide.hasAttribute('hidden')) return currentSlide;

    return slides.find((slide) => !slide.hasAttribute('hidden') && slide.getAttribute('aria-hidden') === 'false');
  }

  function getMediaIdFromSlide(slide) {
    return slide?.dataset?.gullyoMediaId || '';
  }

  function getGallerySlideImage(slide) {
    return slide?.querySelector('img.product-media__image, .product-media img, img') || null;
  }

  function warmGallerySlide(slide, options = {}) {
    const image = getGallerySlideImage(slide);
    if (!(image instanceof HTMLImageElement)) return Promise.resolve();

    image.loading = 'eager';
    if (options.highPriority) {
      image.fetchPriority = 'high';
    }

    if (image.complete && image.naturalWidth > 0) {
      return typeof image.decode === 'function' ? image.decode().catch(() => undefined) : Promise.resolve();
    }

    const loaded = new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
    const decoded = typeof image.decode === 'function' ? image.decode().catch(() => undefined) : loaded;

    return Promise.race([
      decoded,
      loaded,
      new Promise((resolve) => window.setTimeout(resolve, options.timeout || 1800)),
    ]);
  }

  function warmGalleryNeighbors(slides, ordered, filteredIndex) {
    if (ordered.length <= 1) return;

    [-1, 1].forEach((offset) => {
      const neighborIndex = (filteredIndex + offset + ordered.length) % ordered.length;
      const neighborSlide = slides[ordered[neighborIndex]];
      void warmGallerySlide(neighborSlide);
    });
  }

  function setGalleryActiveMedia(gallery, mediaId, options = {}) {
    if (!gallery || !mediaId) return;
    gallery.__gullyoActiveMediaId = String(mediaId);

    if (options.lock !== false) {
      gallery.__gullyoSelectionLockUntil = performance.now() + GULLYO_SELECTION_LOCK_MS;
    }
  }

  function getGalleryActiveMediaId(gallery) {
    return gallery?.__gullyoActiveMediaId || '';
  }

  function isGallerySelectionLocked(gallery) {
    return Boolean(gallery?.__gullyoSelectionLockUntil && performance.now() < gallery.__gullyoSelectionLockUntil);
  }

  function getVisibleSlideByMediaId(slides, mediaId) {
    if (!mediaId) return null;
    return slides.find(
      (slide) => String(getMediaIdFromSlide(slide)) === String(mediaId) && isVariantVisibleSlide(slide)
    );
  }

  function syncGalleryThumbnails(gallery, slides, activeRef, options = {}) {
    let activeMediaId = '';

    if (typeof activeRef === 'number') {
      activeMediaId = getMediaIdFromSlide(slides[activeRef]);
    } else if (activeRef && typeof activeRef === 'object' && 'dataset' in activeRef) {
      activeMediaId = getMediaIdFromSlide(activeRef);
    } else if (activeRef != null && activeRef !== '') {
      activeMediaId = String(activeRef);
    }

    syncGallerySlidesAccessibility(slides, activeMediaId);

    gallery.querySelectorAll('slideshow-controls[thumbnails]').forEach((controls) => {
      controls.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumb, thumbIndex) => {
        const slide = slides[thumbIndex];
        const thumbMediaId = thumb.dataset.mediaId || getMediaIdFromSlide(slide);
        const visible = Boolean(slide && isVariantVisibleSlide(slide) && !thumb.hidden);
        const selected =
          visible && activeMediaId && thumbMediaId && String(thumbMediaId) === String(activeMediaId);

        thumb.setAttribute('aria-current', selected ? 'true' : 'false');
        thumb.removeAttribute('aria-selected');
        if (selected) {
          if (options.scroll !== false) {
            scrollThumbnailIntoStrip(thumb, options.scrollBehavior);
          }
        }
      });
    });
  }

  function syncGallerySlidesAccessibility(slides, activeMediaId) {
    let resolvedActiveMediaId = activeMediaId;
    if (!resolvedActiveMediaId) {
      resolvedActiveMediaId = getMediaIdFromSlide(slides.find(isVariantVisibleSlide));
    }

    slides.forEach((slide) => {
      const active =
        isVariantVisibleSlide(slide) &&
        Boolean(resolvedActiveMediaId) &&
        String(getMediaIdFromSlide(slide)) === String(resolvedActiveMediaId);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      if ('inert' in slide) {
        slide.inert = !active;
      }
    });
  }

  function updateGalleryNavVisibility(gallery, slides) {
    const ordered = getGalleryOrderedIndices(gallery);
    const singleImage = ordered.length <= 1;
    gallery.querySelectorAll('slideshow-arrows .slideshow-control').forEach((control) => {
      control.disabled = false;
      control.hidden = singleImage;
      control.setAttribute('aria-hidden', singleImage ? 'true' : 'false');
    });
  }

  function selectGallerySlide(slideshow, slide, event, options = {}) {
    if (!slide || typeof slideshow?.select !== 'function') return;

    const slideId = slide.getAttribute('slide-id');
    if (slideId) {
      slideshow.select({ id: slideId }, event, { animate: options.animate !== false });
      return;
    }

    const filteredIndex = slideshow.slides?.indexOf(slide);
    if (filteredIndex >= 0) {
      slideshow.select(filteredIndex, event, { animate: options.animate !== false });
    }
  }

  function getGalleryActiveFilteredIndex(gallery, slides, slideshow) {
    const ordered = getGalleryOrderedIndices(gallery);
    if (!ordered.length) return 0;

    const activeMediaId = getGalleryActiveMediaId(gallery);
    const activeSlide = getVisibleSlideByMediaId(slides, activeMediaId);
    if (activeSlide) {
      const activeAbsolute = slides.indexOf(activeSlide);
      const activeFiltered = ordered.indexOf(activeAbsolute);
      if (activeFiltered >= 0) return activeFiltered;
    }

    const currentSlide = getVisibleSlideFromSlideshow(slideshow, slides);
    if (!currentSlide) return 0;

    const absoluteIndex = slides.indexOf(currentSlide);
    const filteredIndex = ordered.indexOf(absoluteIndex);
    return filteredIndex >= 0 ? filteredIndex : 0;
  }

  function showGalleryAtFilteredIndex(gallery, slideshow, slides, filteredIndex, options = {}) {
    const ordered = getGalleryOrderedIndices(gallery);
    if (!ordered.length || !slideshow) return;

    const safeIndex = ((filteredIndex % ordered.length) + ordered.length) % ordered.length;
    const absoluteIndex = ordered[safeIndex];
    const targetSlide = slides[absoluteIndex];
    if (!targetSlide) return;

    // Start the fetch immediately, but never make interaction wait for image
    // decoding. The fixed-ratio slide remains stable while the browser paints.
    void warmGallerySlide(targetSlide, { highPriority: true });
    const targetMediaId = getMediaIdFromSlide(targetSlide);
    setGalleryActiveMedia(gallery, targetMediaId, { lock: options.lock !== false });
    selectGallerySlide(slideshow, targetSlide, undefined, options);
    syncGalleryThumbnails(gallery, slides, targetMediaId, {
      scroll: options.scrollThumbnail !== false,
      scrollBehavior: options.scrollBehavior,
    });
    gallery.__gullyoActiveFilteredIndex = safeIndex;
    updateGalleryNavVisibility(gallery, slides);
    warmGalleryNeighbors(slides, ordered, safeIndex);
  }

  function navigateGalleryFiltered(gallery, slideshow, slides, delta, options = {}) {
    const ordered = getGalleryOrderedIndices(gallery);
    if (!ordered.length || ordered.length <= 1 || !slideshow) return;

    const currentFiltered = getGalleryActiveFilteredIndex(gallery, slides, slideshow);
    const nextFiltered = (currentFiltered + delta + ordered.length) % ordered.length;
    showGalleryAtFilteredIndex(gallery, slideshow, slides, nextFiltered, options);
  }

  function bindGallerySwipe(gallery, slideshow, slides) {
    const surface =
      gallery.querySelector('slideshow-component [ref="scroller"]') ||
      gallery.querySelector('slideshow-component .slideshow__slides') ||
      gallery.querySelector('slideshow-component');
    if (!surface || surface.dataset.gullyoSwipeBound) return;

    surface.dataset.gullyoSwipeBound = 'true';
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let startScrollTop = 0;

    surface.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        startScrollLeft = surface.scrollLeft || 0;
        startScrollTop = surface.scrollTop || 0;
      },
      { passive: true }
    );

    surface.addEventListener(
      'touchend',
      (event) => {
        if (event.changedTouches.length !== 1) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        const scrollDelta =
          Math.abs((surface.scrollLeft || 0) - startScrollLeft) + Math.abs((surface.scrollTop || 0) - startScrollTop);
        if (scrollDelta > 8) return;
        if (Math.abs(dx) < GULLYO_SWIPE_MIN || Math.abs(dy) > Math.abs(dx)) return;
        navigateGalleryFiltered(gallery, slideshow, slides, dx < 0 ? 1 : -1, {
          animate: false,
          scrollBehavior: 'auto',
        });
      },
      { passive: true }
    );
  }

  function initPdpGalleryKeyboard(section, gallery, slideshow, slides) {
    if (pdpGalleryKeyboardBound) return;
    pdpGalleryKeyboardBound = true;

    document.addEventListener('keydown', (event) => {
      if (getOpenGullyoImageViewer()) return;
      if (!section?.isConnected || !gallery?.isConnected) return;
      if (isKeyboardTargetIgnored(document.activeElement)) return;
      if (!isElementInViewport(gallery)) return;

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        navigateGalleryFiltered(gallery, slideshow, slides, 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        navigateGalleryFiltered(gallery, slideshow, slides, -1);
      }
    });
  }

  let pdpVariantListenerBound = false;

  function initPdpGallery(section, gallery) {
    if (gallery.dataset.gullyoGalleryInit === 'true') return;

    const slideshow = gallery.querySelector('slideshow-component');
    const slides = getGallerySlides(gallery);
    if (!slides.length || !slideshow) return;

    gallery.dataset.gullyoGalleryInit = 'true';

    let thumbHandlersBound = false;
    let galleryArrowsBound = false;
    let slideshowSelectBound = false;

    function bindThumbnailHandlers() {
      if (thumbHandlersBound) return;
      thumbHandlersBound = true;

      const warmThumbnail = (event) => {
        const thumb = event.target.closest('.slideshow-controls__thumbnail');
        if (!thumb || !gallery.contains(thumb) || thumb.hidden) return;

        const mediaId = thumb.dataset.mediaId;
        const slide = mediaId
          ? slides.find((item) => getMediaIdFromSlide(item) === String(mediaId))
          : null;
        if (!isVariantVisibleSlide(slide)) return;

        void warmGallerySlide(slide, { highPriority: event.type === 'pointerdown' });
      };

      gallery.addEventListener('pointerover', warmThumbnail, { passive: true });
      gallery.addEventListener('focusin', warmThumbnail);
      gallery.addEventListener('pointerdown', warmThumbnail, { capture: true, passive: true });

      gallery.addEventListener(
        'click',
        (event) => {
          const thumb = event.target.closest('.slideshow-controls__thumbnail');
          if (!thumb || !gallery.contains(thumb) || thumb.hidden) return;

          const mediaId = thumb.dataset.mediaId;
          const controls = thumb.closest('slideshow-controls[thumbnails]');
          const controlThumbs = controls
            ? Array.from(controls.querySelectorAll('.slideshow-controls__thumbnail'))
            : [];
          const thumbIndex = controlThumbs.indexOf(thumb);
          const slide =
            (mediaId && slides.find((item) => getMediaIdFromSlide(item) === String(mediaId))) ||
            slides[thumbIndex];

          if (!isVariantVisibleSlide(slide)) return;

          const ordered = getGalleryOrderedIndices(gallery);
          const absoluteIndex = slides.indexOf(slide);
          const filteredIndex = ordered.indexOf(absoluteIndex);
          if (filteredIndex < 0) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          showGalleryAtFilteredIndex(gallery, slideshow, slides, filteredIndex, {
            animate: false,
            scrollThumbnail: true,
            scrollBehavior: 'auto',
          });
        },
        true
      );
    }

    function bindSlideshowSelectSync() {
      if (slideshowSelectBound || !slideshow) return;
      slideshowSelectBound = true;

      slideshow.addEventListener('slideshow:select', (event) => {
        const slide = event.detail?.slide;
        const mediaId = getMediaIdFromSlide(slide);
        if (!mediaId) return;
        if (!isVariantVisibleSlide(slide)) return;

        if (
          event.detail?.trigger === 'scroll' &&
          isGallerySelectionLocked(gallery) &&
          getGalleryActiveMediaId(gallery) &&
          String(mediaId) !== String(getGalleryActiveMediaId(gallery))
        ) {
          const activeSlide = getVisibleSlideByMediaId(slides, getGalleryActiveMediaId(gallery));
          if (activeSlide) {
            selectGallerySlide(slideshow, activeSlide, undefined, { animate: false });
            syncGalleryThumbnails(gallery, slides, getGalleryActiveMediaId(gallery), { scroll: false });
          }
          return;
        }

        setGalleryActiveMedia(gallery, mediaId, { lock: event.detail?.trigger !== 'scroll' });
        const shouldFollowThumbnail =
          event.detail?.userInitiated === true ||
          event.detail?.trigger === 'scroll' ||
          event.detail?.trigger === 'drag';
        syncGalleryThumbnails(gallery, slides, mediaId, {
          scroll: shouldFollowThumbnail,
          scrollBehavior:
            event.detail?.trigger === 'scroll' || event.detail?.trigger === 'drag' ? 'auto' : undefined,
        });

        const ordered = getGalleryOrderedIndices(gallery);
        const absoluteIndex = slides.indexOf(slide);
        const filteredIndex = ordered.indexOf(absoluteIndex);
        if (filteredIndex >= 0) {
          gallery.__gullyoActiveFilteredIndex = filteredIndex;
        }
      });
    }

    function bindGalleryArrowHandlers() {
      if (galleryArrowsBound) return;
      galleryArrowsBound = true;

      gallery.addEventListener(
        'click',
        (event) => {
          const previous = event.target.closest('.slideshow-control--previous');
          const next = event.target.closest('.slideshow-control--next');
          if (!previous && !next) return;
          if (getGalleryOrderedIndices(gallery).length <= 1) return;

          // The base slideshow owns PDP arrow clicks through its document-level
          // delegated handler. Do not advance the gallery a second time.
          if (event.defaultPrevented) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          navigateGalleryFiltered(gallery, slideshow, slides, previous ? -1 : 1, {
            scrollBehavior: 'auto',
          });
        },
        { capture: true }
      );
    }

    function applyVariantGallery(variant) {
      const variantKey = getVariantKeyFromResource(variant);
      const firstVariantSlide = slides.find((slide) =>
        variantKeysMatch(slide.dataset.gullyoVariantKey || '', variantKey)
      );

      void warmGallerySlide(firstVariantSlide, { highPriority: true });

      slides.forEach((slide, index) => {
        const mediaKey = slide.dataset.gullyoVariantKey || '';
        const visible = variantKeysMatch(mediaKey, variantKey);
        slide.toggleAttribute('hidden', !visible);
        if (!visible) {
          slide.setAttribute('aria-hidden', 'true');
        } else {
          slide.removeAttribute('aria-hidden');
        }
      });

      gallery.querySelectorAll('slideshow-controls[thumbnails]').forEach((controls) => {
        controls.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumb, thumbIndex) => {
          const slide = slides[thumbIndex];
          const visible = slide && isMediaItemVisible(slide);
          thumb.hidden = !visible;
          thumb.style.display = visible ? 'inline-flex' : 'none';
          thumb.setAttribute('tabindex', visible ? '0' : '-1');
          thumb.setAttribute('aria-hidden', visible ? 'false' : 'true');
          thumb.setAttribute('aria-current', 'false');
          thumb.removeAttribute('aria-selected');
        });
      });

      cacheGalleryOrderedIndices(gallery, slides);

      showGalleryAtFilteredIndex(gallery, slideshow, slides, 0, {
        animate: false,
        scrollBehavior: 'auto',
      });

      gallery.__gullyoCurrentVariant = variant;
      refreshGullyoImageViewer(gallery);
    }

    bindThumbnailHandlers();
    bindGalleryArrowHandlers();
    bindSlideshowSelectSync();
    bindGallerySwipe(gallery, slideshow, slides);
    initPdpGalleryKeyboard(section, gallery, slideshow, slides);
    initGullyoImageViewer(section, gallery);

    const initialVariant = getInitialPdpVariant(section);
    if (initialVariant) {
      applyVariantGallery(initialVariant);
    } else {
      const initialSlide = getVisibleSlideFromSlideshow(slideshow, slides) || slides.find(isVariantVisibleSlide);
      syncGallerySlidesAccessibility(slides, getMediaIdFromSlide(initialSlide));
      updateGalleryNavVisibility(gallery, slides);
    }

    if (pdpVariantListenerBound) return;
    pdpVariantListenerBound = true;

    document.addEventListener('variant:update', (event) => {
      const pdp = event.target?.closest?.('.product-information') || section;
      const activeGallery = pdp?.querySelector?.('media-gallery[data-gullyo-variant-gallery="true"]');
      if (!activeGallery || !pdp.contains(activeGallery)) return;

      const variant = event.detail?.resource;
      if (!variant) return;

      if (activeGallery.dataset.gullyoGalleryInit !== 'true' || activeGallery !== gallery) {
        initPdpGallery(pdp, activeGallery);
        return;
      }

      applyVariantGallery(variant);
      updatePdpVariantMeta(pdp, variant);
    });
  }

  let pdpStickyBarBound = false;

  function initPdpStickyBar(section) {
    if (pdpStickyBarBound || !section) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        bindPdpStickyBar(section);
      });
    });
  }

  function setStickyAddButtonFeedback(button, state) {
    if (!(button instanceof HTMLButtonElement)) return;

    const labelNode = button.querySelector('.add-to-cart-text__content span span');
    if (!labelNode) return;

    if (!button.dataset.gullyoOriginalLabel) {
      button.dataset.gullyoOriginalLabel = labelNode.textContent.trim() || 'Add to Bag';
    }

    const defaultLabel = button.dataset.gullyoOriginalLabel;
    button.classList.remove('is-adding', 'is-added', 'is-error');

    if (state === 'loading') {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-adding');
      labelNode.textContent = 'Adding…';
      return;
    }

    button.removeAttribute('aria-busy');

    if (state === 'added') {
      button.disabled = true;
      button.classList.add('is-added');
      labelNode.textContent = 'Added ✓';
      return;
    }

    if (state === 'error') {
      button.disabled = false;
      button.classList.add('is-error');
      labelNode.textContent = 'Try again';
      return;
    }

    button.disabled = false;
    labelNode.textContent = defaultLabel;
  }

  function bindPdpStickyBar(section) {
    if (pdpStickyBarBound || !section) return;

    const shopifySection = section.closest('.shopify-section');
    const sticky = shopifySection?.querySelector('sticky-add-to-cart');
    const bar = sticky?.querySelector('[ref="stickyBar"]');
    const purchaseArea =
      section.querySelector('.buy-buttons-block') ||
      section.querySelector('buy-buttons-component') ||
      section.querySelector('.product-information__grid');

    if (!sticky || !bar || !purchaseArea) return;

    pdpStickyBarBound = true;
    sticky.dataset.gullyoStickyInit = 'true';

    bar.classList.remove('is-visible', 'is-footer-near');
    bar.dataset.stuck = 'false';
    bar.setAttribute('aria-hidden', 'true');

    const productForm = section.querySelector('product-form-component');
    const getStickyButton = () => sticky.querySelector('[ref="addToCartButton"]');
    let scrolledPast = false;
    let hiddenByBoundary = false;
    let overlayOpen = false;
    let stickyVisible = false;
    let stickyAddPending = false;
    let stickyFeedbackTimeout;

    const overlaySelector =
      'cart-drawer-component dialog[open], #search-modal dialog[open], #gullyo-account-drawer-dialog[open], .gullyo-image-viewer[open], .header-drawer dialog[open]';

    const syncStickyBarVisibility = () => {
      const shouldShow = scrolledPast && !hiddenByBoundary && !overlayOpen;
      if (shouldShow === stickyVisible) return;

      stickyVisible = shouldShow;
      bar.classList.toggle('is-visible', shouldShow);
      bar.classList.toggle('is-footer-near', hiddenByBoundary);
      bar.dataset.stuck = shouldShow ? 'true' : 'false';
      bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      document.body.classList.toggle('gullyo-sticky-atc-visible', shouldShow);

      const button = getStickyButton();
      if (button) {
        if (shouldShow) button.removeAttribute('tabindex');
        else button.setAttribute('tabindex', '-1');
      }
    };

    const purchaseObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          scrolledPast = false;
        } else {
          scrolledPast = entry.boundingClientRect.bottom < 0;
        }
        syncStickyBarVisibility();
      },
      { threshold: 0, rootMargin: '0px 0px 0px 0px' }
    );
    purchaseObserver.observe(purchaseArea);

    const stickyBoundary =
      document.querySelector('.gullyo-footer') ||
      document.querySelector('.gullyo-footer__bottom') ||
      document.querySelector('footer');
    if (stickyBoundary) {
      const barHeight = Math.ceil(bar.getBoundingClientRect().height || bar.offsetHeight || 88);
      const boundaryObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          hiddenByBoundary = entry.isIntersecting || entry.boundingClientRect.top <= 0;
          syncStickyBarVisibility();
        },
        { threshold: 0, rootMargin: `0px 0px -${barHeight + 20}px 0px` }
      );
      boundaryObserver.observe(stickyBoundary);
    }

    const refreshOverlayState = () => {
      overlayOpen = Boolean(document.querySelector(overlaySelector));
      syncStickyBarVisibility();
    };

    document.addEventListener('dialog:open', refreshOverlayState, true);
    document.addEventListener('dialog:close', refreshOverlayState, true);
    document.addEventListener(
      'toggle',
      (event) => {
        if (event.target instanceof HTMLDialogElement) refreshOverlayState();
      },
      true
    );

    const resetStickyAddFeedback = (state) => {
      stickyAddPending = false;
      if (stickyFeedbackTimeout) {
        clearTimeout(stickyFeedbackTimeout);
        stickyFeedbackTimeout = undefined;
      }

      const stickyButton = getStickyButton();
      if (!(stickyButton instanceof HTMLButtonElement)) return;

      if (state === 'added') {
        setStickyAddButtonFeedback(stickyButton, 'added');
        stickyFeedbackTimeout = window.setTimeout(() => {
          setStickyAddButtonFeedback(stickyButton, 'default');
          if (productForm instanceof HTMLElement) {
            productForm.dataset.gullyoCartProcessing = 'false';
          }
        }, 1500);
        return;
      }

      if (state === 'error') {
        setStickyAddButtonFeedback(stickyButton, 'error');
        stickyFeedbackTimeout = window.setTimeout(() => {
          setStickyAddButtonFeedback(stickyButton, 'default');
          if (productForm instanceof HTMLElement) {
            productForm.dataset.gullyoCartProcessing = 'false';
          }
        }, 1500);
        return;
      }

      setStickyAddButtonFeedback(stickyButton, 'default');
    };

    sticky.addEventListener(
      'click',
      (event) => {
        const stickyButton = event.target.closest('[ref="addToCartButton"]');
        if (!(stickyButton instanceof HTMLButtonElement)) return;

        if (
          stickyAddPending ||
          stickyButton.disabled ||
          productForm?.dataset.gullyoCartProcessing === 'true'
        ) {
          return;
        }

        stickyAddPending = true;
        setStickyAddButtonFeedback(stickyButton, 'loading');

        if (stickyFeedbackTimeout) {
          clearTimeout(stickyFeedbackTimeout);
        }

        stickyFeedbackTimeout = window.setTimeout(() => {
          if (stickyAddPending) resetStickyAddFeedback('default');
        }, 12000);

        window.setTimeout(() => {
          if (stickyAddPending && productForm?.dataset.gullyoCartProcessing !== 'true') {
            resetStickyAddFeedback('default');
          }
        }, 500);
      },
      true
    );

    document.addEventListener('cart:update', (event) => {
      if (!stickyAddPending) return;
      if (event.detail?.data?.didError) {
        resetStickyAddFeedback('error');
        return;
      }
      if (event.detail?.data?.source === 'product-form-component') {
        resetStickyAddFeedback('added');
      }
    });

    document.addEventListener('cart:error', () => {
      if (!stickyAddPending) return;
      resetStickyAddFeedback('error');
    });

    document.addEventListener('variant:update', () => {
      if (stickyAddPending) return;
      const stickyButton = getStickyButton();
      if (stickyButton instanceof HTMLButtonElement) {
        setStickyAddButtonFeedback(stickyButton, 'default');
      }
    });

    syncStickyBarVisibility();
  }

  function initPdp() {
    if (window.__gullyoPdpInit) return;

    const section = document.querySelector('.product-information[data-testid="product-information"]');
    if (!section) return;

    window.__gullyoPdpInit = true;

    initPdpStickyBar(section);
    updatePdpEyebrow(section);
    updatePdpVariantMeta(section, getInitialPdpVariant(section));
    initPdpTrust(section);

    const gallery = section.querySelector('media-gallery[data-gullyo-variant-gallery="true"]');
    if (gallery) {
      initPdpGallery(section, gallery);
    }
  }

  function initTitleRotation() {
    if (window.__gullyoTitleRotationInit) return;
    window.__gullyoTitleRotationInit = true;

    const PHRASES = [
      'Back to the dance floor',
      'Your next pair awaits',
      'See the night differently',
    ];
    const ROTATION_MS = 3000;

    const getBaseTitle = () => {
      const meta = document.querySelector('meta[name="gullyo-page-title"]');
      const fromMeta = meta?.getAttribute('content')?.trim();
      return fromMeta || document.title;
    };

    let intervalId = null;
    let phraseIndex = 0;

    const clearRotation = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    const restoreTitle = () => {
      clearRotation();
      document.title = getBaseTitle();
    };

    const startRotation = () => {
      clearRotation();
      phraseIndex = 0;
      document.title = PHRASES[phraseIndex];
      intervalId = window.setInterval(() => {
        phraseIndex = (phraseIndex + 1) % PHRASES.length;
        document.title = PHRASES[phraseIndex];
      }, ROTATION_MS);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        startRotation();
      } else {
        restoreTitle();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  let gullyoReadyInitialized = false;

  function showCookiePreferences() {
    const privacyApi = window.privacyBanner;
    if (privacyApi && typeof privacyApi.showPreferences === 'function') {
      privacyApi.showPreferences();
      return true;
    }

    const customerPrivacy = window.Shopify?.customerPrivacy;
    if (customerPrivacy && typeof customerPrivacy.showPreferences === 'function') {
      customerPrivacy.showPreferences();
      return true;
    }

    return false;
  }

  function initFooterCookieSettings() {
    if (window.__gullyoFooterCookieSettingsInit) return;
    window.__gullyoFooterCookieSettingsInit = true;

    const openPreferences = () => {
      if (showCookiePreferences()) return;

      if (window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures(
          [
            {
              name: 'consent-tracking-api',
              version: '0.1',
            },
          ],
          (error) => {
            if (!error) {
              showCookiePreferences();
            }
          }
        );
      }
    };

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-gullyo-cookie-settings]');
      if (!trigger) return;

      event.preventDefault();
      openPreferences();
    });
  }

    function initPageBackNavigation() {
      document.querySelectorAll('[data-gullyo-page-back]').forEach((link) => {
        if (!(link instanceof HTMLAnchorElement) || link.dataset.gullyoPageBackBound === 'true') return;
        link.dataset.gullyoPageBackBound = 'true';

        const label = link.querySelector('[data-gullyo-page-back-label]');
        let canGoBack = false;
        try {
          const previousUrl = new URL(document.referrer);
          canGoBack =
            window.history.length > 1 &&
            previousUrl.origin === window.location.origin &&
            previousUrl.href !== window.location.href;
        } catch {
          canGoBack = false;
        }

        if (label) {
          label.textContent = canGoBack ? 'Back' : 'Home';
        }
        link.dataset.gullyoPageBackMode = canGoBack ? 'history' : 'home';

        link.addEventListener('click', (event) => {
          if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            link.dataset.gullyoPageBackMode !== 'history'
          ) {
            return;
          }

          event.preventDefault();
          window.history.back();
        });
    });
  }

  function onReady() {
    if (gullyoReadyInitialized) return;
    gullyoReadyInitialized = true;

    initInitialScrollSystem();
    initHashNavigation();
    initActiveSections();
    initBestsellersCarousel();
    initQuickAdd();
    initSearchPanel();
    initAccountPanel();
    initProductCardSwatches();
    initPdp();
    initTitleRotation();
    initFooterCookieSettings();
    initPageBackNavigation();
    initCultureSoonModal();
    initGullyoAccountDrawer();
    initJudgeMeReviewWidgetCopy();
  }

  function closeOpenGullyoOverlays(except) {
    if (except !== 'cart') {
      document.querySelectorAll('cart-drawer-component').forEach((cartDrawer) => {
        if (typeof cartDrawer.closeDialog === 'function') {
          cartDrawer.closeDialog();
          return;
        }

        const cartDialog = cartDrawer.querySelector('dialog[open]');
        if (cartDialog instanceof HTMLDialogElement) {
          cartDialog.close();
        }
      });
    }

    if (except !== 'search') {
      document.dispatchEvent(new CustomEvent('gullyo:search-closed'));
      const searchModal = document.getElementById('search-modal');
      if (searchModal && typeof searchModal.closeDialog === 'function') {
        searchModal.closeDialog();
      } else {
        searchModal?.querySelector('dialog[open]')?.close();
      }
    }

    if (except !== 'account') {
      const accountDialog = document.getElementById('gullyo-account-drawer-dialog');
      if (accountDialog instanceof HTMLDialogElement && accountDialog.open) {
        accountDialog.dispatchEvent(new CustomEvent('gullyo:account-drawer-request-close'));
      }
    }

    const cultureDialog = document.getElementById('gullyo-culture-soon-dialog');
    if (cultureDialog instanceof HTMLDialogElement && cultureDialog.open) {
      cultureDialog.close();
    }
  }

  function initGullyoAccountDrawer() {
    if (window.__gullyoAccountDrawerInit) return;

    const root = document.getElementById('gullyo-account-drawer');
    const dialog = document.getElementById('gullyo-account-drawer-dialog');
    if (!(root instanceof HTMLElement) || !(dialog instanceof HTMLDialogElement)) return;

    window.__gullyoAccountDrawerInit = true;

    /** @type {HTMLElement | null} */
    let lastTrigger = null;
    /** @type {number} */
    let previousScrollY = 0;
    /** @type {{ keydown: (event: KeyboardEvent) => void; focusin: (event: FocusEvent) => void } | null} */
    let trapHandlers = null;
    /** @type {boolean} */
    let isClosing = false;

    const ACCOUNT_DRAWER_ANIM_MS = 200;
    const ACCOUNT_DRAWER_BACKDROP_MS = 150;
    const ACCOUNT_DRAWER_CLOSE_MS = Math.max(ACCOUNT_DRAWER_ANIM_MS, ACCOUNT_DRAWER_BACKDROP_MS);
    const ACCOUNT_DRAWER_CLOSE_FALLBACK_MS = ACCOUNT_DRAWER_CLOSE_MS + 20;

    const loginUrl = root.dataset.gullyoAccountLoginUrl || '/account/login';
    root.querySelectorAll('[data-gullyo-account-sign-in]').forEach((link) => {
      if (link instanceof HTMLAnchorElement) {
        link.href = loginUrl;
      }
    });

    function setAccountDrawerExpanded(expanded) {
      document.querySelectorAll('[data-gullyo-account-open]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(expanded));
      });
    }

    function getFocusableElements(container) {
      return Array.from(
        container.querySelectorAll(
          "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
        )
      );
    }

    function removeAccountDrawerTrapFocus() {
      if (!trapHandlers) return;
      document.removeEventListener('keydown', trapHandlers.keydown, true);
      document.removeEventListener('focusin', trapHandlers.focusin, true);
      trapHandlers = null;
    }

    function trapAccountDrawerFocus(container) {
      removeAccountDrawerTrapFocus();

      const focusable = getFocusableElements(container);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      trapHandlers = {
        keydown: (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            closeAccountDrawer();
            return;
          }

          if (event.key !== 'Tab') return;

          const activeEl = document.activeElement;
          if (!event.shiftKey && activeEl === last) {
            event.preventDefault();
            first?.focus();
          } else if (event.shiftKey && (activeEl === first || activeEl === container)) {
            event.preventDefault();
            last?.focus();
          }
        },
        focusin: (event) => {
          if (event.target instanceof Node && !container.contains(event.target)) {
            event.stopPropagation();
            first?.focus();
          }
        },
      };

      document.addEventListener('keydown', trapHandlers.keydown, true);
      document.addEventListener('focusin', trapHandlers.focusin, true);
    }

    function lockBodyScroll() {
      previousScrollY = window.scrollY;
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${previousScrollY}px`;
      document.body.classList.add('gullyo-account-drawer-open');
    }

    function unlockBodyScroll() {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.classList.remove('gullyo-account-drawer-open');
      window.scrollTo({ top: previousScrollY, behavior: 'instant' });
    }

    function waitForAccountDrawerCloseAnimation() {
      return new Promise((resolve) => {
        let settled = false;

        const finish = () => {
          if (settled) return;
          settled = true;
          window.clearTimeout(fallbackTimer);
          resolve(undefined);
        };

        const fallbackTimer = window.setTimeout(finish, ACCOUNT_DRAWER_CLOSE_FALLBACK_MS);

        window.requestAnimationFrame(() => {
          const animationPromises = dialog
            .getAnimations()
            .filter((animation) => animation.timeline instanceof DocumentTimeline)
            .map((animation) => animation.finished.catch(() => undefined));

          if (animationPromises.length) {
            Promise.allSettled(animationPromises).then(finish);
          }
        });
      });
    }

    async function closeAccountDrawer() {
      if (!dialog.open || isClosing) return;
      isClosing = true;

      dialog.style.animation = 'none';
      void dialog.offsetWidth;
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      await waitForAccountDrawerCloseAnimation();

      removeAccountDrawerTrapFocus();
      unlockBodyScroll();
      dialog.classList.remove('dialog-closing');
      dialog.style.animation = '';
      if (dialog.open) {
        dialog.close();
      }
      setAccountDrawerExpanded(false);
      focusWithoutScroll(lastTrigger);
      lastTrigger = null;
      isClosing = false;
    }

    function openAccountDrawer(trigger) {
      if (dialog.open || isClosing) return;

      closeOpenGullyoOverlays('account');
      lastTrigger = trigger;

      lockBodyScroll();

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      setAccountDrawerExpanded(true);

      const panel = dialog.querySelector('.gullyo-account-drawer__inner');
      if (panel instanceof HTMLElement) {
        trapAccountDrawerFocus(panel);
      }

      const closeButton = dialog.querySelector('.gullyo-account-drawer__close');
      focusWithoutScroll(closeButton instanceof HTMLElement ? closeButton : panel);
    }

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        closeAccountDrawer();
        return;
      }

      if (event.target.closest('[data-gullyo-account-drawer-close]')) {
        event.preventDefault();
        closeAccountDrawer();
      }
    });

    dialog.addEventListener('close', () => {
      removeAccountDrawerTrapFocus();
      unlockBodyScroll();
      dialog.classList.remove('dialog-closing');
      dialog.style.animation = '';
      isClosing = false;
      setAccountDrawerExpanded(false);
    });

    dialog.addEventListener('gullyo:account-drawer-request-close', () => {
      closeAccountDrawer();
    });

    document.addEventListener('keydown', (event) => {
      if (!dialog.open || event.key !== 'Escape') return;
      event.preventDefault();
      closeAccountDrawer();
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-gullyo-account-open]');
      if (!trigger) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();
      openAccountDrawer(trigger);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const trigger = event.target.closest('[data-gullyo-account-open]');
      if (!trigger) return;

      event.preventDefault();
      openAccountDrawer(trigger);
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.search-action, search-button, [data-gullyo-search-toggle]')) return;
      if (dialog.open) {
        closeAccountDrawer();
      }
    }, true);

    document.querySelectorAll('[data-testid="cart-drawer-trigger"], cart-drawer-component button[on\\:click="/open"]').forEach((cartTrigger) => {
      cartTrigger.addEventListener(
        'click',
        () => {
          if (dialog.open) {
            closeAccountDrawer();
          }
        },
        true
      );
    });
  }

  function initCultureSoonModal() {
    if (window.__gullyoCultureSoonInit) return;
    window.__gullyoCultureSoonInit = true;

    let dialog = document.getElementById('gullyo-culture-soon-dialog');
    let lastTrigger = null;
    /** @type {{ keydown: (event: KeyboardEvent) => void; focusin: (event: FocusEvent) => void } | null} */
    let trapHandlers = null;

    if (dialog instanceof HTMLDialogElement) {
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-label', 'GULLYO Culture');
    }

    function getFocusableElements(container) {
      return Array.from(
        container.querySelectorAll(
          "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
        )
      );
    }

    function removeCultureSoonTrapFocus() {
      if (!trapHandlers) return;
      document.removeEventListener('keydown', trapHandlers.keydown, true);
      document.removeEventListener('focusin', trapHandlers.focusin, true);
      trapHandlers = null;
    }

    function trapCultureSoonFocus(container) {
      removeCultureSoonTrapFocus();

      const focusable = getFocusableElements(container);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      trapHandlers = {
        keydown: (event) => {
          if (event.key !== 'Tab') return;

          const activeEl = document.activeElement;
          if (!event.shiftKey && activeEl === last) {
            event.preventDefault();
            first?.focus();
          } else if (event.shiftKey && (activeEl === first || activeEl === container)) {
            event.preventDefault();
            last?.focus();
          }
        },
        focusin: (event) => {
          if (event.target instanceof Node && !container.contains(event.target)) {
            event.stopPropagation();
            first?.focus();
          }
        },
      };

      document.addEventListener('keydown', trapHandlers.keydown, true);
      document.addEventListener('focusin', trapHandlers.focusin, true);
    }

    function resolveCultureSoonTitle(trigger) {
      const slug = trigger.dataset.gullyoCultureSoon || '';
      if (slug === 'gullyo-club') return 'GULLYO Club';

      const label = trigger.getAttribute('aria-label') || trigger.textContent.trim();
      if (label) return `GULLYO Culture — ${label}`;

      const slugLabel = (slug || 'culture').replace(/-/g, ' ');
      const capitalized = slugLabel.charAt(0).toUpperCase() + slugLabel.slice(1);
      return `GULLYO Culture — ${capitalized}`;
    }

    function closeCultureSoonDialog() {
      if (!dialog.open) return;
      dialog.close();
    }

    function openCultureSoonDialog(trigger) {
      lastTrigger = trigger;

      const titleNode = dialog.querySelector('[data-gullyo-culture-soon-title]');
      const resolvedTitle = resolveCultureSoonTitle(trigger);
      if (titleNode) {
        titleNode.textContent = resolvedTitle;
      }
      dialog.setAttribute('aria-label', resolvedTitle);

      const panel = dialog.querySelector('.gullyo-culture-soon__panel');
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }

      if (panel instanceof HTMLElement) {
        trapCultureSoonFocus(panel);
      }

      const closeButton = dialog.querySelector('.gullyo-culture-soon__close');
      focusWithoutScroll(closeButton);
    }

    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.id = 'gullyo-culture-soon-dialog';
      dialog.className = 'gullyo-culture-soon';
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', 'gullyo-culture-soon-title');
      dialog.setAttribute('aria-label', 'GULLYO Culture');
      dialog.innerHTML = `
        <div class="gullyo-culture-soon__panel" tabindex="-1">
          <button type="button" class="gullyo-culture-soon__close" data-gullyo-culture-soon-close aria-label="Close dialog">×</button>
          <h2 id="gullyo-culture-soon-title" class="gullyo-display gullyo-culture-soon__title" data-gullyo-culture-soon-title>GULLYO Culture</h2>
          <p class="gullyo-culture-soon__body">This part of the GULLYO world is still being built. House music, events, stories, and community drops will arrive soon.</p>
          <button type="button" class="gullyo-culture-soon__cta gullyo-eyebrow" data-gullyo-culture-soon-close>Back to GULLYO</button>
        </div>
      `;
      document.body.appendChild(dialog);
    }

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        closeCultureSoonDialog();
        return;
      }

      if (event.target.closest('[data-gullyo-culture-soon-close]')) {
        event.preventDefault();
        closeCultureSoonDialog();
      }
    });

    dialog.addEventListener('close', () => {
      removeCultureSoonTrapFocus();
      focusWithoutScroll(lastTrigger);
      lastTrigger = null;
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeCultureSoonDialog();
    });

    document.addEventListener(
      'keydown',
      (event) => {
        if (!dialog.open || event.key !== 'Escape') return;
        event.preventDefault();
        closeCultureSoonDialog();
      },
      true
    );

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-gullyo-culture-soon]');
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();
      openCultureSoonDialog(trigger);
    });
  }

  function initJudgeMeReviewWidgetCopy() {
    if (window.__gullyoJudgeMeCopyInit) return;

    const root = document.getElementById('judgeme_product_reviews');
    if (!root) return;

    const syncModalAccessibility = () => {
      const modal = document.querySelector('body > .jdgm-review-widget-modal.jdgm-write-review-modal');
      if (!(modal instanceof HTMLElement)) return;

      const isOpen = modal.style.display === 'block';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      modal.inert = !isOpen;
    };

    const applyCopy = () => {
      if (window.jdgmSettings) {
        window.jdgmSettings.widget_no_items_text = 'No reviews yet';
      }

      root.querySelectorAll('.jdgm-empty-state .jm-text').forEach((element) => {
        if (element.textContent.trim() === 'No items found') {
          element.textContent = 'No reviews yet';
        }
      });

      syncModalAccessibility();
    };

    const finish = () => {
      if (window.__gullyoJudgeMeCopyInit) return;
      window.__gullyoJudgeMeCopyInit = true;
      applyCopy();

      const modal = document.querySelector('body > .jdgm-review-widget-modal.jdgm-write-review-modal');
      if (modal instanceof HTMLElement) {
        const modalObserver = new MutationObserver(syncModalAccessibility);
        modalObserver.observe(modal, { attributes: true, attributeFilter: ['style'] });
      }
    };

    if (root.classList.contains('jdgm--done-setup-widget')) {
      finish();
      return;
    }

    window.addEventListener(
      'load',
      () => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(finish);
        });
      },
      { once: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }
})();
